import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bold,
  BookOpen,
  FileText,
  GripVertical,
  Image as ImageIcon,
  Indent,
  LayoutGrid,
  List,
  MessageCircle,
  Outdent,
  Plus,
  RotateCcw,
  Search,
  Smartphone,
  Trash2,
  X,
} from "lucide-react";

import { useAuth } from "../../../../auth/useAuth";
import sermonHeaderBar from "../../../../assets/backgrounds/Sermon Header Bar.png";
import SermonIcon from "../../../../assets/logos/Sermon Icon.png";
import GlobalNotificationPill from "../../../../core/components/GlobalNotificationPill";
import PanelLoadingState from "../../../../core/components/PanelLoadingState";
import useResponsive from "../../../../core/hooks/useResponsive";
import { updateChurchLiveDisplay } from "../../../church/services/liveDisplayService";
import { getServiceResumeIndexAfterSermonBlock } from "../../../church/services/serviceService";
import {
  buildTitleSlide,
  createDraftFromSermon,
  createCustomSlideItem,
  createHeaderBlockItem,
  createTextSlideItem,
  createVisualSlideItem,
  deleteSermon,
  fetchScriptureSlides,
  getDefaultSermonDraft,
  getSermonVisualSlideImageUrl,
  isSermonOwnedByOwner,
  listPastSermons,
  listSharedSermons,
  loadSermonDraft,
  pushSermonToService,
  saveSermonDraft,
  saveSermonLive,
  saveSermonToLibrary,
  uploadSermonVisualSlideImage,
} from "../../../church/services/sermonService";

const SERMON_BRAND = "#385cff";
const QUICK_SERMON_SOURCE_PREFIX = "quick-sermon:";
const SERMON_PAGE_STATE_PREFIX = "oikos.church.sermon.pageState";

function createScriptureItem(reference, translationKey, response) {
  return {
    id: crypto.randomUUID(),
    type: "scripture",
    title: reference,
    reference,
    translationKey,
    resolution: response.resolution,
    warning: response.warning || "",
    slides: response.slides,
  };
}

function getTimelineItems(draft) {
  return [buildTitleSlide(draft), ...draft.items];
}

function getDisplayTitle(draft) {
  return draft.title?.trim() || "Untitled Sermon";
}

function getDisplayMeta(draft) {
  return `${draft.sermonDate || "No date"} · ${draft.speakerName || "No speaker"}`;
}

function getInitials(name = "") {
  return String(name || "Sermon user")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "SU";
}

function formatSermonDate(value = "") {
  if (!value) return "No date";

  try {
    const stringValue = String(value);
    const dateValue = stringValue.includes("T")
      ? new Date(stringValue)
      : new Date(`${stringValue}T00:00:00`);

    if (Number.isNaN(dateValue.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(dateValue);
  } catch {
    return value;
  }
}

function getSermonPreview(sermon = {}) {
  const firstItem = (sermon.items || [])[0];
  if (firstItem?.reference) return firstItem.reference;
  return "";
}

function getAuthorName(sermon = {}, fallback = "Unknown author") {
  return sermon.authorName || sermon.author_name || fallback;
}

function withDefaultSpeaker(draft = {}, speakerName = "") {
  return {
    ...draft,
    speakerName: draft.speakerName?.trim() ? draft.speakerName : speakerName,
  };
}

function hasDraftContent(draft) {
  return Boolean(
    draft.title?.trim() ||
      draft.items?.length
  );
}

function getSermonPageStateKey(owner = {}) {
  return `${SERMON_PAGE_STATE_PREFIX}.${owner.storageKey || owner.userId || owner.orgUserId || "guest"}`;
}

function loadSermonPageState(owner) {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(getSermonPageStateKey(owner));
    return raw ? JSON.parse(raw) : {};
  } catch (_error) {
    return {};
  }
}

function saveSermonPageState(owner, state = {}) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      getSermonPageStateKey(owner),
      JSON.stringify({
        ...loadSermonPageState(owner),
        ...state,
        updatedAt: new Date().toISOString(),
      })
    );
  } catch (_error) {
    // Best-effort only. Losing this should never interrupt sermon editing.
  }
}

function stripHtmlToText(value = "") {
  if (typeof window === "undefined") {
    return String(value).replace(/<[^>]*>/g, " ").trim();
  }

  const element = window.document.createElement("div");
  element.innerHTML = value;
  return (element.textContent || element.innerText || "").trim();
}

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function getHeaderBlockTextStyle(value = "") {
  const textLength = String(value || "").length;

  if (textLength > 54) return { fontSize: 22, lineHeight: 1.02 };
  if (textLength > 42) return { fontSize: 26, lineHeight: 1.02 };
  if (textLength > 32) return { fontSize: 32, lineHeight: 1.02 };
  return {};
}

function getEnabledSlideCount(item) {
  if (item.type === "title") {
    return item.includeInSlideshow === false ? 0 : 1;
  }

  if (item.type === "custom" || item.type === "header") {
    return 0;
  }

  if (item.type === "text") {
    return item.includeInSlideshow === false || (!item.title && !item.body) ? 0 : 1;
  }

  if (item.type === "visual") {
    return item.imageUrl || item.imagePath ? 1 : 0;
  }

  return (item.slides || []).filter((slide) => slide.enabled !== false).length;
}

function getVisualImageStyle(item = {}) {
  const positionX = Number.isFinite(Number(item.imagePositionX)) ? Number(item.imagePositionX) : 50;
  const positionY = Number.isFinite(Number(item.imagePositionY)) ? Number(item.imagePositionY) : 50;

  return {
    objectFit: item.imageFit === "cover" ? "cover" : "contain",
    objectPosition: `${positionX}% ${positionY}%`,
  };
}

function buildThoughtBubbleHtml(selectedText = "", thoughtNumber = 1) {
  const markerNumber = Math.max(1, Number(thoughtNumber) || 1);
  const thoughtMarker =
    '<span contenteditable="false" style="align-items:center;background:#f97316;border:2px solid #ffffff;border-radius:999px;box-shadow:0 0 0 4px rgba(249,115,22,0.2);color:#ffffff;display:inline-flex;font-size:10px;font-weight:950;height:18px;justify-content:center;line-height:1;margin:0 5px 0 0;vertical-align:1px;width:18px;">' +
    markerNumber +
    '</span>';
  const anchoredText = selectedText
    ? thoughtMarker + escapeHtml(selectedText)
    : thoughtMarker;

  return [
    '<span data-sermon-thought-anchor="true" dir="ltr" style="background:#ffedd5;border:1px solid #fb923c;border-left:4px solid #f97316;border-radius:8px;box-shadow:0 0 0 3px rgba(249,115,22,0.16);color:#9a3412;direction:ltr;font-weight:950;margin:0 3px;padding:2px 7px;unicode-bidi:isolate;">',
    anchoredText,
    '</span>',
    '<span data-sermon-thought-bubble="true" dir="ltr" style="background:#fff7ed;border:1px solid #fed7aa;border-radius:24px;box-shadow:0 12px 24px rgba(124,45,18,0.12);box-sizing:border-box;clear:right;color:#7c2d12;direction:ltr;display:block;float:right;font-weight:800;line-height:1.35;margin:0 -286px 14px 24px;max-width:228px;min-height:92px;padding:16px 18px;position:relative;text-align:left;unicode-bidi:isolate;white-space:normal;width:228px;">',
    '<span contenteditable="false" style="background:#fb923c;border-radius:999px;box-shadow:0 0 0 3px rgba(249,115,22,0.12);height:2px;left:-56px;position:absolute;top:35px;width:56px;"></span>',
    '<span contenteditable="false" style="align-items:center;background:#f97316;border:2px solid #fff7ed;border-radius:999px;color:#ffffff;display:flex;font-size:10px;font-weight:950;height:22px;justify-content:center;left:-13px;line-height:1;position:absolute;top:25px;width:22px;">',
    markerNumber,
    '</span>',
    '<span contenteditable="false" style="color:#c2410c;display:block;font-size:11px;font-weight:950;letter-spacing:0.08em;margin-bottom:4px;text-transform:uppercase;">Thought ',
    markerNumber,
    '</span>',
    'Type your thought here...',
    '</span>&nbsp;',
  ].join("");
}

function NoteBlockEditor({ itemId, body, isEmpty, onChange }) {
  const editorRef = useRef(null);
  const isFocusedRef = useRef(false);

  useLayoutEffect(() => {
    const editor = editorRef.current;
    const nextBody = body || "";

    if (!editor || isFocusedRef.current) {
      return;
    }

    if (editor.innerHTML !== nextBody) {
      editor.innerHTML = nextBody;
    }
  }, [body, itemId]);

  const syncBody = (event) => {
    onChange(event.currentTarget.innerHTML);
  };

  return (
    <div
      ref={editorRef}
      contentEditable
      data-note-editor-id={itemId}
      onClick={(event) => event.stopPropagation()}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onBlur={(event) => {
        isFocusedRef.current = false;
        syncBody(event);
      }}
      onInput={syncBody}
      onPaste={(event) => {
        event.preventDefault();
        const text = event.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
        syncBody(event);
      }}
      style={{
        ...styles.noteBlockTextarea,
        ...(isEmpty ? styles.noteBlockTextareaEmpty : {}),
      }}
      suppressContentEditableWarning
      spellCheck="true"
    />
  );
}

function buildLiveDisplaySlideFromSermonItem(item = {}, draft = {}, overrides = {}) {
  if (item.type === "title") {
    const title = getDisplayTitle(draft);
    const subtitle = draft.speakerName || "";
    const date = draft.sermonDate || "";

    return {
      id: "title-slide-live",
      parentId: item.id || "title-slide",
      itemType: "title_slide",
      title,
      subtitle,
      date,
      body: [subtitle, date].filter(Boolean).join(" • "),
      sermonTitle: title,
      sourceType: "sermon-title",
    };
  }

  if (item.type === "scripture") {
    const enabledSlides = (item.slides || []).filter((slide) => slide.enabled !== false);
    const verseText = enabledSlides
      .map((slide) => `${slide.verseNumber ? `${slide.verseNumber} ` : ""}${slide.text || ""}`.trim())
      .filter(Boolean)
      .join("\n\n");

    if (!verseText) {
      return null;
    }

    return {
      id: `${item.id || "sermon"}-live-slide`,
      parentId: item.id || "",
      itemType: "verse",
      title: item.reference || item.title || "Scripture",
      reference: item.reference || item.title || "",
      verseNumber: "",
      text: verseText,
      body: verseText,
      sermonTitle: draft.title || "",
      sourceType: "sermon-scripture",
    };
  }

  if (item.type === "visual") {
    const imageUrl = overrides.imageUrl || item.imageUrl || "";

    if (!imageUrl) {
      return null;
    }

    return {
      id: `${item.id || "visual"}-live-slide`,
      parentId: item.id || "",
      itemType: "visual_slide",
      title: item.title || "Visual Slide",
      subtitle: item.caption || "",
      body: item.caption || "",
      imageUrl,
      imagePath: item.imagePath || "",
      storageBucket: item.storageBucket || "",
      imageFit: item.imageFit || "contain",
      imagePositionX: Number.isFinite(Number(item.imagePositionX)) ? Number(item.imagePositionX) : 50,
      imagePositionY: Number.isFinite(Number(item.imagePositionY)) ? Number(item.imagePositionY) : 50,
      sermonTitle: draft.title || "",
      sourceType: "sermon-visual",
    };
  }

  if (item.type === "text") {
    const bodyHtml = item.body || "";
    const body = stripHtmlToText(bodyHtml);

    if (!item.title && !body) {
      return null;
    }

    return {
      id: `${item.id || "text"}-live-slide`,
      parentId: item.id || "",
      itemType: "custom_slide",
      title: item.title || "Text Slide",
      subtitle: "",
      body,
      bodyHtml,
      text: body,
      sermonTitle: draft.title || "",
      sourceType: "sermon-text",
    };
  }

  return null;
}

export default function SermonPage({
  initialViewMode = "dashboard",
  useStandaloneRoutes = false,
}) {
  const { user } = useAuth();
  const { isPhone } = useResponsive();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState(initialViewMode);
  const [draft, setDraft] = useState(getDefaultSermonDraft());
  const [selectedItemId, setSelectedItemId] = useState("title-slide");
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("Saved");
  const [statusMessage, setStatusMessage] = useState("");
  const [scriptureForm, setScriptureForm] = useState({
    reference: "",
    translationKey: "NKJV",
  });
  const [isScriptureModalOpen, setIsScriptureModalOpen] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [isAddingScripture, setIsAddingScripture] = useState(false);
  const [pushState, setPushState] = useState("");
  const [pastSermons, setPastSermons] = useState([]);
  const [sharedSermons, setSharedSermons] = useState([]);
  const [pastLoading, setPastLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [actionSermon, setActionSermon] = useState(null);
  const [liveDisplayStatus, setLiveDisplayStatus] = useState("");
  const [activeLiveDisplayItemId, setActiveLiveDisplayItemId] = useState("");
  const [serviceResume, setServiceResume] = useState(null);
  const [draftBeforeSavedLiveView, setDraftBeforeSavedLiveView] = useState(null);
  const [draggedBlockId, setDraggedBlockId] = useState("");
  const [dragOverBlockId, setDragOverBlockId] = useState("");
  const [isCreateTileHovered, setIsCreateTileHovered] = useState(false);
  const [hoveredSermonTileId, setHoveredSermonTileId] = useState("");
  const skipNextDraftAutosaveRef = useRef(false);
  const closingSermonRef = useRef(false);

  const actorName =
    user?.churchOrgSession?.orgUser?.displayName ||
    user?.churchOrgSession?.orgUser?.display_name ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email ||
    "Sermon user";
  const workspaceName =
    user?.churchOrgSession?.account?.name ||
    user?.account?.name ||
    user?.organization?.name ||
    "Workspace";
  const sermonOwner = useMemo(() => {
    const orgUser = user?.churchOrgSession?.orgUser || null;
    const account =
      user?.churchOrgSession?.account ||
      user?.account ||
      user?.organization ||
      null;

    return {
      userId: user?.isChurchOrgUser ? "" : user?.id || "",
      orgUserId: orgUser?.id || "",
      accountId: account?.id || "",
      sessionToken: user?.churchOrgSession?.sessionToken || "",
      authorName: actorName,
      storageKey: orgUser?.id
        ? `org.${orgUser.id}`
        : user?.id
          ? `auth.${user.id}`
          : "guest",
    };
  }, [actorName, user]);

  useEffect(() => {
    let mounted = true;

    async function bootstrap() {
      setLoading(true);

      try {
        const [nextDraft, nextPastSermons, nextSharedSermons] = await Promise.all([
          loadSermonDraft(sermonOwner),
          listPastSermons(sermonOwner),
          listSharedSermons(sermonOwner),
        ]);

        if (!mounted) return;

        const restoredDraft = withDefaultSpeaker(nextDraft, actorName);
        const restoredState = loadSermonPageState(sermonOwner);
        const restoredViewMode =
          initialViewMode === "dashboard" &&
          hasDraftContent(restoredDraft) &&
          ["builder", "live"].includes(restoredState.viewMode)
            ? restoredState.viewMode
            : initialViewMode;

        setDraft(restoredDraft);
        setPastSermons(nextPastSermons);
        setSharedSermons(nextSharedSermons);
        setSelectedItemId(restoredState.selectedItemId || "title-slide");
        setViewMode(restoredViewMode);
      } catch (error) {
        console.error("Sermon page bootstrap error:", error);

        if (!mounted) return;

        setStatusMessage("We could not load your sermons. Starting fresh.");
        setDraft(withDefaultSpeaker(getDefaultSermonDraft(), actorName));
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    bootstrap();

    return () => {
      mounted = false;
    };
  }, [actorName, initialViewMode, sermonOwner]);

  useEffect(() => {
    if (initialViewMode !== "dashboard") {
      setViewMode(initialViewMode);
    }
  }, [initialViewMode]);

  useEffect(() => {
    let mounted = true;

    getServiceResumeIndexAfterSermonBlock(user?.id)
      .then((resume) => {
        if (mounted) {
          setServiceResume(resume);
        }
      })
      .catch((error) => {
        console.error("Service resume preload error:", error);
      });

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (loading) return;

    saveSermonPageState(sermonOwner, {
      selectedItemId,
      viewMode,
    });
  }, [loading, selectedItemId, sermonOwner, viewMode]);

  useEffect(() => {
    if (!statusMessage) return undefined;

    const timeout = window.setTimeout(() => {
      setStatusMessage("");
    }, 4500);

    return () => window.clearTimeout(timeout);
  }, [statusMessage]);

  useEffect(() => {
    if (loading || closingSermonRef.current) return;

    if (skipNextDraftAutosaveRef.current) {
      skipNextDraftAutosaveRef.current = false;
      return;
    }

    if (!hasDraftContent(draft)) {
      setSaveState("Saved");
      return;
    }

    setSaveState("Saving...");

    const timeout = window.setTimeout(async () => {
      try {
        await saveSermonLive(sermonOwner, draft);
        setSaveState("Saved");
      } catch (error) {
        console.error("Sermon draft save error:", error);
        setSaveState("Save failed");
      }
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [draft, loading, sermonOwner]);

  useEffect(() => {
    if (loading || closingSermonRef.current) return undefined;

    const persistCurrentSermon = () => {
      saveSermonPageState(sermonOwner, {
        selectedItemId,
        viewMode,
      });

      if (hasDraftContent(draft)) {
        saveSermonLive(sermonOwner, draft).catch((error) => {
          console.error("Sermon draft background save error:", error);
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        persistCurrentSermon();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", persistCurrentSermon);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", persistCurrentSermon);
    };
  }, [draft, loading, selectedItemId, sermonOwner, viewMode]);

  const timelineItems = useMemo(() => getTimelineItems(draft), [draft]);
  const selectedItem =
    timelineItems.find((item) => item.id === selectedItemId) || timelineItems[0];
  const liveDisplayStatusIsError =
    liveDisplayStatus.toLowerCase().includes("could not") ||
    liveDisplayStatus.toLowerCase().includes("does not");

  const refreshPastSermons = async () => {
    setPastLoading(true);

    try {
      const [nextPastSermons, nextSharedSermons] = await Promise.all([
        listPastSermons(sermonOwner),
        listSharedSermons(sermonOwner),
      ]);
      setPastSermons(nextPastSermons);
      setSharedSermons(nextSharedSermons);
    } catch (error) {
      console.error("Past sermons refresh error:", error);
    } finally {
      setPastLoading(false);
    }
  };

  const updateTitleField = (field, value) => {
    setDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateTimelineItem = (itemId, updater) => {
    setDraft((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === itemId ? { ...item, ...updater(item) } : item
      ),
    }));
  };

  const reorderItems = (itemId, direction) => {
    setDraft((current) => {
      const items = [...current.items];
      const index = items.findIndex((item) => item.id === itemId);

      if (index === -1) {
        return current;
      }

      const targetIndex = direction === "up" ? index - 1 : index + 1;

      if (targetIndex < 0 || targetIndex >= items.length) {
        return current;
      }

      const [moved] = items.splice(index, 1);
      items.splice(targetIndex, 0, moved);

      return {
        ...current,
        items,
      };
    });
  };

  const reorderItemsByDrop = (draggedItemId, targetItemId) => {
    if (!draggedItemId || !targetItemId || draggedItemId === targetItemId) {
      return;
    }

    setDraft((current) => {
      const items = [...current.items];
      const fromIndex = items.findIndex((item) => item.id === draggedItemId);
      const toIndex = items.findIndex((item) => item.id === targetItemId);

      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return current;
      }

      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);

      return {
        ...current,
        items,
      };
    });
  };

  const handleBlockDragStart = (event, itemId) => {
    event.stopPropagation();
    setDraggedBlockId(itemId);
    setDragOverBlockId("");
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", itemId);
  };

  const handleBlockDragOver = (event, itemId) => {
    if (!draggedBlockId || draggedBlockId === itemId) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverBlockId(itemId);
  };

  const handleBlockDrop = (event, itemId) => {
    event.preventDefault();
    event.stopPropagation();

    const draggedItemId = event.dataTransfer.getData("text/plain") || draggedBlockId;
    reorderItemsByDrop(draggedItemId, itemId);
    setDraggedBlockId("");
    setDragOverBlockId("");
  };

  const handleBlockDragEnd = () => {
    setDraggedBlockId("");
    setDragOverBlockId("");
  };

  const removeTimelineItem = (itemId) => {
    setDraft((current) => ({
      ...current,
      items: current.items.filter((item) => item.id !== itemId),
    }));

    setSelectedItemId((currentSelected) =>
      currentSelected === itemId ? "title-slide" : currentSelected
    );
  };

  const updateTitleSlideEnabled = (enabled) => {
    setDraft((current) => ({
      ...current,
      titleSlideEnabled: enabled,
    }));
  };

  const updateScriptureSlideEnabled = (itemId, slideId, enabled) => {
    updateTimelineItem(itemId, (item) => ({
      slides: (item.slides || []).map((slide) =>
        slide.id === slideId ? { ...slide, enabled } : slide
      ),
    }));
  };

  const syncNoteEditorBody = (itemId) => {
    if (typeof document === "undefined") {
      return;
    }

    const editor = document.querySelector(`[data-note-editor-id="${itemId}"]`);

    if (!editor) {
      return;
    }

    updateTimelineItem(itemId, () => ({ body: editor.innerHTML }));
  };

  const applyNoteFormat = (itemId, command, value = null) => {
    if (typeof document === "undefined") {
      return;
    }

    document.execCommand(command, false, value);
    syncNoteEditorBody(itemId);
  };

  const insertThoughtBubble = (itemId) => {
    if (typeof document === "undefined") {
      return;
    }

    const editor = document.querySelector(`[data-note-editor-id="${itemId}"]`);
    const selection = window.getSelection?.();
    const hasEditorSelection =
      editor &&
      selection &&
      selection.rangeCount > 0 &&
      editor.contains(selection.anchorNode) &&
      editor.contains(selection.focusNode);
    const selectedText = hasEditorSelection ? selection.toString().trim() : "";
    const thoughtNumber = editor
      ? editor.querySelectorAll('[data-sermon-thought-bubble="true"]').length + 1
      : 1;

    if (editor && !hasEditorSelection) {
      editor.focus();
    }

    document.execCommand("insertHTML", false, buildThoughtBubbleHtml(selectedText, thoughtNumber));
    syncNoteEditorBody(itemId);
  };

  const openDashboard = () => {
    if (draftBeforeSavedLiveView) {
      setDraft(draftBeforeSavedLiveView);
      setDraftBeforeSavedLiveView(null);
      setSelectedItemId("title-slide");
    }

    setViewMode("dashboard");
    setActionSermon(null);
    setLiveDisplayStatus("");

    if (useStandaloneRoutes) {
      navigate("/sermon");
      return;
    }
  };

  const handleCloseBuilder = async () => {
    closingSermonRef.current = true;
    setSaveState("Saving...");

    try {
      if (hasDraftContent(draft)) {
        await saveSermonToLibrary(sermonOwner, draft, { saveSharedCopy: false });
        await refreshPastSermons();
        setStatusMessage(
          draft.sourceSermonId && !isSermonOwnedByOwner(sermonOwner, draft)
            ? `Closed "${getDisplayTitle(draft)}" without saving it to your sermon library.`
            : `"${getDisplayTitle(draft)}" was saved to your sermon library.`
        );
      } else {
        await deleteSermon(sermonOwner, {
          ...draft,
          isDraft: true,
        });
        setStatusMessage("");
      }

      skipNextDraftAutosaveRef.current = true;
      setDraft(withDefaultSpeaker(getDefaultSermonDraft(), actorName));
      setSelectedItemId("title-slide");
      openDashboard();
    } catch (error) {
      console.error("Close sermon save error:", error);
      setStatusMessage(error.message || "We could not save that sermon before closing.");
    } finally {
      setSaveState("Saved");
      closingSermonRef.current = false;
    }
  };

  const openBuilder = () => {
    setViewMode("builder");

    if (useStandaloneRoutes) {
      navigate("/sermon/editor");
      return;
    }
  };

  const handleNewSermon = async () => {
    const nextDraft = withDefaultSpeaker(getDefaultSermonDraft(), actorName);
    setDraft(nextDraft);
    setSelectedItemId("title-slide");
    setScriptureForm({
      reference: "",
      translationKey: "NKJV",
    });
    setIsScriptureModalOpen(false);
    setStatusMessage("Started a new sermon draft.");

    openBuilder();
  };

  const handleEditSermon = async (sermon) => {
    const nextDraft = createDraftFromSermon(sermon);
    setDraft(nextDraft);
    setActionSermon(null);
    setSelectedItemId("title-slide");
    setStatusMessage(`Opened "${getDisplayTitle(sermon)}" in the builder.`);

    if (useStandaloneRoutes) {
      try {
        await saveSermonDraft(sermonOwner, nextDraft);
      } catch (error) {
        console.error("Open sermon draft save error:", error);
      }
    }

    openBuilder();
  };

  const handleOpenLiveView = (sermon = draft) => {
    const nextDraft = sermon === draft ? draft : createDraftFromSermon(sermon);
    const isViewingSavedSermon = sermon !== draft && !sermon.isDraft;

    if (isViewingSavedSermon) {
      skipNextDraftAutosaveRef.current = true;
      setDraftBeforeSavedLiveView(draft);
    } else {
      setDraftBeforeSavedLiveView(null);
    }

    setDraft(nextDraft);
    setActionSermon(null);
    setSelectedItemId("title-slide");
    setActiveLiveDisplayItemId("");
    setLiveDisplayStatus("");
    setViewMode("live");
    setStatusMessage("");
  };

  const handleSendSermonBlockToLiveDisplay = async (item) => {
    if (item?.type === "custom") {
      setLiveDisplayStatus("Note blocks stay in the iPad viewer and do not go to Live Display.");
      return;
    }

    let imageUrl = "";

    if (item?.type === "visual") {
      try {
        imageUrl = await getSermonVisualSlideImageUrl(item);
      } catch (error) {
        console.error("Resolve visual slide image error:", error);
        imageUrl = item.imageUrl || "";
      }
    }

    const liveSlide = buildLiveDisplaySlideFromSermonItem(item, draft, { imageUrl });

    if (!liveSlide) {
      setLiveDisplayStatus("This sermon block does not have anything to show yet.");
      return;
    }

    const liveDisplayItemId = item.id || "sermon-block";
    const displayLabel =
      item.type === "title" ? "title slide" : item.reference || item.title || "sermon block";

    setActiveLiveDisplayItemId(liveDisplayItemId);
    setLiveDisplayStatus(`Showing ${displayLabel} on Live Display.`);

    try {
      await updateChurchLiveDisplay(user, null, {
        selectedServiceItemId: `${QUICK_SERMON_SOURCE_PREFIX}${liveDisplayItemId}`,
        liveSlides: [liveSlide],
        currentSlideIndex: 0,
        isReady: true,
        isLive: true,
        state: "live",
        fast: true,
      });
    } catch (error) {
      console.error("Send sermon block to live display error:", error);
      setActiveLiveDisplayItemId((current) =>
        current === liveDisplayItemId ? "" : current
      );
      setLiveDisplayStatus(error.message || "Could not send this sermon block to Live Display.");
    }
  };

  const handleReturnLiveDisplayToLoop = () => {
    setActiveLiveDisplayItemId("");
    setLiveDisplayStatus("Returning Live Display to the pre-service loop...");

    updateChurchLiveDisplay(user, null, {
      selectedServiceItemId: "all-service-items",
      liveSlides: [],
      state: "loop",
      isLive: false,
      isReady: false,
      currentSlideIndex: 0,
      fast: true,
    })
      .then(() => {
        setLiveDisplayStatus("Live Display is back on the pre-service loop.");
      })
      .catch((error) => {
        console.error("Return live display to loop error:", error);
        setLiveDisplayStatus(error.message || "Could not return Live Display to the loop.");
      });
  };

  const handleReturnLiveDisplayToServiceSlides = async () => {
    setActiveLiveDisplayItemId("");
    setLiveDisplayStatus("Returning Live Display to service slides...");

    try {
      const resume = serviceResume || await getServiceResumeIndexAfterSermonBlock(user?.id);
      if (!serviceResume) {
        setServiceResume(resume);
      }
      await updateChurchLiveDisplay(user, null, {
        selectedServiceItemId: "all-service-items",
        liveSlides: resume.slides,
        state: "live",
        isReady: true,
        isLive: true,
        currentSlideIndex: resume.currentSlideIndex,
        fast: true,
      });
      setLiveDisplayStatus("Live Display is back on service slides.");
    } catch (error) {
      console.error("Return live display to service slides error:", error);
      setLiveDisplayStatus(error.message || "Could not return Live Display to service slides.");
    }
  };

  const handleDeleteSermon = async (sermon) => {
    try {
      await deleteSermon(sermonOwner, sermon);

      if (sermon.isDraft) {
        setDraft(withDefaultSpeaker(getDefaultSermonDraft(), actorName));
        setSelectedItemId("title-slide");
      } else {
        await refreshPastSermons();
      }

      setStatusMessage(
        `"${getDisplayTitle(sermon)}" was removed from your sermon library.`
      );
      setActionSermon(null);
    } catch (error) {
      console.error("Delete sermon error:", error);
      setStatusMessage(error.message || "We could not delete that sermon.");
    }
  };

  const handleDeleteCurrentSermon = async () => {
    const title = getDisplayTitle(draft);
    const shouldDelete =
      typeof window === "undefined" ||
      window.confirm(`Delete "${title}"? This cannot be undone.`);

    if (!shouldDelete) {
      return;
    }

    try {
      const sourceSermonId = draft.sourceSermonId || "";

      if (sourceSermonId && isSermonOwnedByOwner(sermonOwner, draft)) {
        await deleteSermon(sermonOwner, {
          ...draft,
          id: sourceSermonId,
          archiveId: draft.sourceArchiveId || sourceSermonId,
          isDraft: false,
        });
      }

      if (draft.id && draft.id !== sourceSermonId) {
        await deleteSermon(sermonOwner, {
          ...draft,
          isDraft: true,
        });
      }

      skipNextDraftAutosaveRef.current = true;
      setDraft(withDefaultSpeaker(getDefaultSermonDraft(), actorName));
      setSelectedItemId("title-slide");
      setStatusMessage(`"${title}" was removed from your sermon library.`);
      await refreshPastSermons();
      openDashboard();
    } catch (error) {
      console.error("Delete current sermon error:", error);
      setStatusMessage(error.message || "We could not delete that sermon.");
    }
  };

  const handleAddNoteBlockLegacy = () => {
    const nextItem = createCustomSlideItem();

    setDraft((current) => ({
      ...current,
      items: [...current.items, nextItem],
    }));
    setSelectedItemId(nextItem.id);
  };

  const handleAddNoteBlock = () => {
    const nextItem = {
      ...createCustomSlideItem(),
      title: "Note Block",
      body: "",
      includeInSlideshow: false,
    };

    setDraft((current) => ({
      ...current,
      items: [...current.items, nextItem],
    }));
    setSelectedItemId(nextItem.id);
    setStatusMessage("Added a note block.");
  };

  const handleAddHeaderBlock = () => {
    const nextItem = createHeaderBlockItem();

    setDraft((current) => ({
      ...current,
      items: [...current.items, nextItem],
    }));
    setSelectedItemId(nextItem.id);
    setStatusMessage("Added a header block.");
  };

  const handleAddTextSlide = () => {
    const nextItem = createTextSlideItem();

    setDraft((current) => ({
      ...current,
      items: [...current.items, nextItem],
    }));
    setSelectedItemId(nextItem.id);
    setStatusMessage("Added a text slide.");
  };

  const handleAddVisualSlide = () => {
    const nextItem = createVisualSlideItem();

    setDraft((current) => ({
      ...current,
      items: [...current.items, nextItem],
    }));
    setSelectedItemId(nextItem.id);
    setStatusMessage("Added a visual slide.");
  };

  const handleVisualSlideImageFile = async (itemId, file) => {
    if (!file) return;

    setStatusMessage("Preparing visual slide image...");

    try {
      const uploadedImage = await uploadSermonVisualSlideImage(sermonOwner, itemId, file);
      updateTimelineItem(itemId, () => ({
        ...uploadedImage,
        imageName: file.name || "Visual slide image",
      }));
      setStatusMessage("Visual slide image is ready.");
    } catch (error) {
      console.error("Visual slide image upload error:", error);
      setStatusMessage(error.message || "Could not prepare that visual slide image.");
    }
  };

  const handleAddScripture = async () => {
    if (!scriptureForm.reference.trim()) {
      setStatusMessage("Add a scripture reference first.");
      return;
    }

    setIsAddingScripture(true);
    setStatusMessage("");

    try {
      const response = await fetchScriptureSlides({
        reference: scriptureForm.reference.trim(),
        translationKey: scriptureForm.translationKey,
        manualText: "",
      });

      const nextItem = createScriptureItem(
        scriptureForm.reference.trim(),
        scriptureForm.translationKey,
        response
      );

      setDraft((current) => ({
        ...current,
        defaultTranslation: scriptureForm.translationKey,
        items: [...current.items, nextItem],
      }));
      setSelectedItemId(nextItem.id);
      setScriptureForm((current) => ({
        ...current,
        reference: "",
      }));
      setIsScriptureModalOpen(false);
      setStatusMessage(
        response.warning ||
          `Added ${response.slides.length} scripture slide${
            response.slides.length === 1 ? "" : "s"
          }.`
      );
    } catch (error) {
      console.error("Scripture add error:", error);
      setStatusMessage(error.message || "We could not add that scripture block.");
    } finally {
      setIsAddingScripture(false);
    }
  };

  const renderScriptureModal = () => {
    if (!isScriptureModalOpen) {
      return null;
    }

    return (
      <div
        style={styles.actionModalBackdrop}
        onClick={() => {
          if (!isAddingScripture) setIsScriptureModalOpen(false);
        }}
      >
        <section
          style={styles.scriptureModal}
          role="dialog"
          aria-modal="true"
          aria-label="Add verse"
          onClick={(event) => event.stopPropagation()}
        >
          <header style={styles.scriptureModalHeader}>
            <div style={styles.scriptureModalTitleBlock}>
              <span style={styles.scriptureModalIcon}>
                <BookOpen size={22} />
              </span>
              <div>
                <h2 style={styles.scriptureModalTitle}>Add Verse</h2>
                <p style={styles.scriptureModalMeta}>Choose a passage and translation.</p>
              </div>
            </div>
            <button
              type="button"
              style={styles.actionModalClose}
              onClick={() => setIsScriptureModalOpen(false)}
              disabled={isAddingScripture}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </header>

          <div style={styles.scriptureModalFields}>
            <label style={styles.scriptureField}>
              <span style={styles.scriptureFieldLabel}>Reference</span>
              <input
                value={scriptureForm.reference}
                onChange={(event) =>
                  setScriptureForm((current) => ({
                    ...current,
                    reference: event.target.value,
                  }))
                }
                style={styles.scriptureInput}
                placeholder="Romans 6:1-4"
                autoFocus
              />
              <span style={styles.scriptureFieldHint}>Use a single verse or range.</span>
            </label>

            <label style={styles.scriptureField}>
              <span style={styles.scriptureFieldLabel}>Translation</span>
              <select
                value={scriptureForm.translationKey}
                onChange={(event) =>
                  setScriptureForm((current) => ({
                    ...current,
                    translationKey: event.target.value,
                  }))
                }
                style={styles.scriptureSelect}
              >
                {["NKJV", "KJV", "NASB"].map((translationKey) => (
                  <option key={translationKey} value={translationKey}>
                    {translationKey}
                  </option>
                ))}
              </select>
            </label>

          </div>

          <div style={styles.scriptureModalActions}>
            <button
              type="button"
              style={styles.scriptureSecondaryButton}
              onClick={() => setIsScriptureModalOpen(false)}
              disabled={isAddingScripture}
            >
              Cancel
            </button>
            <button
              type="button"
              style={styles.scripturePrimaryButton}
              onClick={handleAddScripture}
              disabled={isAddingScripture}
            >
              <Plus size={16} />
              {isAddingScripture ? "Adding..." : "Add Verse"}
            </button>
          </div>
        </section>
      </div>
    );
  };

  const handlePushToService = async (sourceDraft = draft) => {
    setPushState("Sending to service...");
    setStatusMessage("");

    try {
      const result = await pushSermonToService(sermonOwner, sourceDraft);
      await refreshPastSermons();
      setPushState("");
      setStatusMessage(
        `Added to service: ${result.itemCount} item${
          result.itemCount === 1 ? "" : "s"
        } sent to ${result.serviceId}.`
      );
    } catch (error) {
      console.error("Push to service error:", error);
      setPushState("");
      setStatusMessage(
        error.message || "We could not push the sermon into service items."
      );
    }
  };

  const mySermonLibrary = [
    ...pastSermons.map((sermon) => ({
      ...sermon,
      isDraft: false,
      userId: sermon.userId,
      authorName: getAuthorName(sermon, actorName),
    })),
  ];

  const otherSermonLibrary = sharedSermons.map((sermon) => ({
    ...sermon,
    isDraft: false,
    authorName: getAuthorName(sermon),
  }));

  const matchesSermonQuery = (sermon) => {
    const haystack = [
      getDisplayTitle(sermon),
      sermon.speakerName,
      sermon.sermonDate,
      getAuthorName(sermon),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(query.trim().toLowerCase());
  };
  const filteredMySermons = mySermonLibrary.filter(matchesSermonQuery);
  const filteredOtherSermons = otherSermonLibrary.filter(matchesSermonQuery);

  function renderSermonLaunchCard(sermon) {
    const editedAt = sermon.updatedAt || sermon.updated_at || sermon.archivedAt || sermon.archived_at || sermon.sermonDate;
    const sermonTileId = sermon.id || sermon.archiveId || getDisplayTitle(sermon);
    const isHovered = hoveredSermonTileId === sermonTileId;

    return (
      <button
        className="sermon-launch-tile"
        key={`saved-${sermonTileId}`}
        type="button"
        style={{
          ...styles.recentCard,
          ...(isHovered ? styles.recentCardHover : {}),
        }}
        onClick={() => setActionSermon(sermon)}
        onMouseEnter={() => setHoveredSermonTileId(sermonTileId)}
        onMouseLeave={() => setHoveredSermonTileId("")}
        onFocus={() => setHoveredSermonTileId(sermonTileId)}
        onBlur={() => setHoveredSermonTileId("")}
      >
        <span style={styles.recentCardTop}>
          <span style={styles.recentIconWrap}>
            <img src={SermonIcon} alt="" style={styles.recentIcon} />
          </span>
          <small style={styles.sharedBadge}>Saved Sermon</small>
        </span>
        <strong style={styles.recentCardTitle}>{getDisplayTitle(sermon)}</strong>
        <span style={styles.recentCardDetailPanel}>
          <span style={styles.recentCardDetailRow}>
            <span style={styles.recentCardDetailLabel}>Author</span>
            <span style={styles.recentCardDetailValue}>{getAuthorName(sermon, actorName)}</span>
          </span>
          <span style={styles.recentCardDetailRow}>
            <span style={styles.recentCardDetailLabel}>Edited</span>
            <span style={styles.recentCardDetailValue}>{formatSermonDate(editedAt)}</span>
          </span>
        </span>
      </button>
    );
  }

  function renderCreateSermonCard() {
    return (
      <button
        className="sermon-launch-tile"
        key="create-sermon"
        type="button"
        style={{
          ...styles.recentCard,
          ...styles.createSermonCard,
          ...(isCreateTileHovered ? styles.createSermonCardHover : {}),
        }}
        onClick={handleNewSermon}
        onMouseEnter={() => setIsCreateTileHovered(true)}
        onMouseLeave={() => setIsCreateTileHovered(false)}
        onFocus={() => setIsCreateTileHovered(true)}
        onBlur={() => setIsCreateTileHovered(false)}
      >
        <span
          style={{
            ...styles.createSermonPlusButton,
            ...(isCreateTileHovered ? styles.createSermonPlusButtonHover : {}),
          }}
        >
          <Plus size={44} strokeWidth={2.8} />
        </span>
        <strong style={styles.recentCardTitle}>Create sermon</strong>
      </button>
    );
  }

  if (loading) {
    return (
      <PanelLoadingState
        iconSrc={SermonIcon}
        iconAlt=""
        title="Loading Sermons"
        detail="Restoring your sermon dashboard and preparing the builder..."
      />
    );
  }

  if (viewMode === "dashboard") {
    return (
      <main style={styles.launchPage}>
        <section style={styles.launchHeader}>
          <img src={sermonHeaderBar} alt="" style={styles.launchHeaderBackdrop} />
          <span style={styles.launchHeaderOverlay} />
          <div style={styles.launchHeaderTop}>
            <div style={styles.launchHeaderBrand}>
              <span style={styles.launchHeaderIconWrap}>
                <img src={SermonIcon} alt="" style={styles.launchHeaderIcon} />
              </span>
              <span style={styles.launchHeaderTitleStack}>
                <span style={styles.launchHeaderTitle}>Sermons</span>
                <span style={styles.launchHeaderOrg}>{workspaceName}</span>
              </span>
            </div>
            <div style={styles.launchUserStack}>
              <div style={styles.launchUserTile}>
                <span style={styles.avatar}>{getInitials(actorName)}</span>
                <div style={styles.launchUserCopy}>
                  <strong>{actorName}</strong>
                </div>
              </div>
              <button type="button" style={styles.launchAppsButton} onClick={() => navigate("/church")}>
                <LayoutGrid size={17} />
                All Apps
              </button>
            </div>
          </div>
          <div style={styles.launchHeaderTools}>
            <label style={styles.launchSearchBox}>
              <Search size={17} />
              <input
                style={styles.launchSearchInput}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search sermons"
              />
            </label>
            <button type="button" style={styles.launchNewButton} onClick={handleNewSermon}>
              <img src={SermonIcon} alt="" style={styles.launchNewButtonIcon} />
              Create sermon
            </button>
          </div>
        </section>

        {statusMessage ? <div style={styles.launchNotice}>{statusMessage}</div> : null}

        <section style={styles.launchContent}>
          <section style={styles.launchSection}>
            <div style={styles.launchSectionHeader}>
              <div style={styles.sectionPill}>
                <h2 style={styles.sectionTitle}>My sermons</h2>
                <span style={styles.sectionCount}>{mySermonLibrary.length}</span>
              </div>
            </div>
            <div style={styles.recentGrid}>
              {renderCreateSermonCard()}
              {filteredMySermons.map((sermon) => renderSermonLaunchCard(sermon))}
              {mySermonLibrary.length > 0 && !filteredMySermons.length ? (
                <div style={styles.emptyRecent}>No matching sermons.</div>
              ) : null}
            </div>
          </section>

          <section style={styles.launchSection}>
            <div style={styles.launchSectionHeader}>
              <div style={styles.sectionPill}>
                <h2 style={styles.sectionTitle}>Other sermons</h2>
                <span style={styles.sectionCount}>{otherSermonLibrary.length}</span>
              </div>
            </div>
            <div style={styles.recentGrid}>
              {filteredOtherSermons.map((sermon) => renderSermonLaunchCard(sermon))}
              {!otherSermonLibrary.length ? (
                <div style={styles.emptyRecent}>No sermons from other users yet.</div>
              ) : null}
              {otherSermonLibrary.length > 0 && !filteredOtherSermons.length ? (
                <div style={styles.emptyRecent}>No matching sermons from other users.</div>
              ) : null}
            </div>
          </section>
        </section>

        {actionSermon ? (
          <div style={styles.actionModalBackdrop} onClick={() => setActionSermon(null)}>
            <section
              style={styles.actionModal}
              role="dialog"
              aria-modal="true"
              aria-label={`${getDisplayTitle(actionSermon)} sermon actions`}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={styles.actionModalHeader}>
                <span style={styles.actionModalIconWrap}>
                  <img src={SermonIcon} alt="" style={styles.actionModalIcon} />
                </span>
                <button
                  type="button"
                  style={styles.actionModalClose}
                  onClick={() => setActionSermon(null)}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div style={styles.actionModalBody}>
                <small style={styles.sharedBadge}>Saved Sermon</small>
                <h2 style={styles.actionModalTitle}>{getDisplayTitle(actionSermon)}</h2>
                <p style={styles.actionModalMeta}>
                  By {getAuthorName(actionSermon, actorName)} • {formatSermonDate(actionSermon.sermonDate)}
                </p>
              </div>

              <div style={styles.actionModalActions}>
                <button
                  type="button"
                  style={styles.actionModalSecondary}
                  onClick={() => handleEditSermon(actionSermon)}
                >
                  <FileText size={17} />
                  Edit
                </button>
                <button
                  type="button"
                  style={styles.actionModalPrimary}
                  onClick={() => handleOpenLiveView(actionSermon)}
                >
                  <Smartphone size={17} />
                  Go Live
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </main>
    );
  }

  if (viewMode === "builder") {
    return (
      <main style={styles.sermonEditorPage}>
        <section style={styles.sermonEditorShell}>
          <header style={styles.sermonEditorTopbar}>
            <div style={styles.documentTitleRow}>
              <button
                type="button"
                style={styles.documentTitleLogoButton}
                onClick={handleCloseBuilder}
                title="Back to Sermons"
              >
                <img src={SermonIcon} alt="" style={styles.documentTitleLogo} />
              </button>
              <span style={styles.documentTitleDivider} />
              <img src={SermonIcon} alt="" style={styles.documentTitleIcon} />
              <div style={styles.documentNameBlock}>
                <input
                  style={styles.titleInput}
                  value={draft.title}
                  onChange={(event) => updateTitleField("title", event.target.value)}
                  placeholder="Untitled sermon"
                />
                <span style={styles.ownerLine}>
                  {workspaceName} • {saveState}
                </span>
              </div>
            </div>

            <div style={styles.editorActionGroup}>
              <button
                type="button"
                style={styles.editorGhostButton}
                onClick={handleCloseBuilder}
              >
                <X size={16} />
                Close
              </button>
              <button
                type="button"
                style={styles.editorDangerButton}
                onClick={handleDeleteCurrentSermon}
              >
                <Trash2 size={16} />
                Delete
              </button>
              <button
                type="button"
                style={styles.editorPrimaryButton}
                onClick={() => handleOpenLiveView(draft)}
              >
                <Smartphone size={17} />
                iPad Viewer
              </button>
            </div>
          </header>

          <div style={styles.editorToolbar} aria-label="Sermon builder tools">
            <div style={styles.editorToolGroup}>
              <button
                type="button"
                style={styles.editorToolButton}
                onClick={() => setIsScriptureModalOpen(true)}
                disabled={isAddingScripture}
              >
                <BookOpen size={16} />
                {isAddingScripture ? "Adding..." : "Add Verse"}
              </button>
              <button type="button" style={styles.editorToolButton} onClick={handleAddNoteBlock}>
                <FileText size={16} />
                Add Note Block
              </button>
              <button type="button" style={styles.editorToolButton} onClick={handleAddHeaderBlock}>
                <FileText size={16} />
                Add Header Block
              </button>
              <button type="button" style={styles.editorToolButton} onClick={handleAddTextSlide}>
                <FileText size={16} />
                Add Text Slide
              </button>
              <button type="button" style={styles.editorToolButton} onClick={handleAddVisualSlide}>
                <ImageIcon size={16} />
                Add Visual Slide
              </button>
            </div>
            <div style={styles.editorMetaGroup}>
              <label style={styles.paperField}>
                <span>Speaker</span>
                <input
                  style={{ ...styles.paperInput, ...styles.paperSpeakerInput }}
                  value={draft.speakerName}
                  onChange={(event) => updateTitleField("speakerName", event.target.value)}
                  placeholder="Speaker name"
                />
              </label>
              <label style={styles.paperField}>
                <span>Date</span>
                <input
                  style={{ ...styles.paperInput, ...styles.paperDateInput }}
                  type="date"
                  value={draft.sermonDate}
                  onChange={(event) => updateTitleField("sermonDate", event.target.value)}
                />
              </label>
              <label style={styles.metaToggle}>
                <span>Show in slides</span>
                <input
                  type="checkbox"
                  checked={draft.titleSlideEnabled !== false}
                  onChange={(event) => updateTitleSlideEnabled(event.target.checked)}
                  style={styles.switchInput}
                />
                <span
                  style={{
                    ...styles.switchTrack,
                    ...(draft.titleSlideEnabled !== false ? styles.switchTrackOn : {}),
                  }}
                >
                  <span
                    style={{
                      ...styles.switchThumb,
                      ...(draft.titleSlideEnabled !== false ? styles.switchThumbOn : {}),
                    }}
                  />
                </span>
              </label>
            </div>
          </div>

          {statusMessage ? <div style={styles.editorNotice}>{statusMessage}</div> : null}
          {renderScriptureModal()}

          <div style={{ ...styles.sermonWorkspace, ...(isPhone ? styles.sermonWorkspacePhone : {}) }}>
            <aside
              style={{
                ...styles.quickAddRail,
                ...(isPhone ? styles.quickAddRailPhone : {}),
              }}
              aria-label="Building tools"
            >
              <div style={styles.quickAddRailLabel}>Building Tools</div>
              <button
                type="button"
                style={{
                  ...styles.quickAddButton,
                  ...(isPhone ? styles.quickAddButtonPhone : {}),
                }}
                onClick={() => setIsScriptureModalOpen(true)}
                disabled={isAddingScripture}
              >
                <BookOpen size={17} />
                {isAddingScripture ? "Adding..." : "Verse"}
              </button>
              <button
                type="button"
                style={{
                  ...styles.quickAddButton,
                  ...(isPhone ? styles.quickAddButtonPhone : {}),
                }}
                onClick={handleAddNoteBlock}
              >
                <FileText size={17} />
                Note Block
              </button>
              <button
                type="button"
                style={{
                  ...styles.quickAddButton,
                  ...(isPhone ? styles.quickAddButtonPhone : {}),
                }}
                onClick={handleAddHeaderBlock}
              >
                <FileText size={17} />
                Header Block
              </button>
              <button
                type="button"
                style={{
                  ...styles.quickAddButton,
                  ...(isPhone ? styles.quickAddButtonPhone : {}),
                }}
                onClick={handleAddTextSlide}
              >
                <FileText size={17} />
                Text Slide
              </button>
              <button
                type="button"
                style={{
                  ...styles.quickAddButton,
                  ...(isPhone ? styles.quickAddButtonPhone : {}),
                }}
                onClick={handleAddVisualSlide}
              >
                <ImageIcon size={17} />
                Visual Slide
              </button>
            </aside>
            <section style={styles.sermonDocumentPane}>
              <div style={styles.sermonPaper}>
                <div style={styles.titleSlideBlock}>
                  <div>
                    <div style={styles.blockEyebrow}>Title slide</div>
                    {isTitleEditing ? (
                      <input
                        style={styles.paperTitleInput}
                        value={draft.title}
                        onBlur={() => setIsTitleEditing(false)}
                        onChange={(event) => updateTitleField("title", event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.currentTarget.blur();
                          }
                        }}
                        placeholder="Untitled sermon"
                        autoFocus
                      />
                    ) : (
                      <button
                        type="button"
                        style={styles.paperTitleButton}
                        onClick={() => setIsTitleEditing(true)}
                      >
                        {draft.title || "Untitled Sermon"}
                      </button>
                    )}
                    <p style={styles.paperSubtitle}>
                      {draft.speakerName || "Speaker name"} • {formatSermonDate(draft.sermonDate)}
                    </p>
                  </div>
                </div>

                <div style={styles.blockList}>
                  {draft.items.length ? draft.items.map((item, index) => {
                    const isSelected = selectedItemId === item.id;

                    if (item.type === "scripture") {
                      return (
                        <article
                          key={item.id}
                          onDragOver={(event) => handleBlockDragOver(event, item.id)}
                          onDrop={(event) => handleBlockDrop(event, item.id)}
                          style={{
                            ...styles.sermonBlock,
                            ...(isSelected ? styles.sermonBlockSelected : {}),
                            ...(draggedBlockId === item.id ? styles.sermonBlockDragging : {}),
                            ...(dragOverBlockId === item.id ? styles.sermonBlockDragOver : {}),
                          }}
                          onClick={() => setSelectedItemId(item.id)}
                        >
                          <div style={styles.blockHeader}>
                            <div>
                              <div style={styles.blockEyebrow}>Verse block {index + 1}</div>
                              <input
                                value={item.reference}
                                onChange={(event) =>
                                  updateTimelineItem(item.id, () => ({
                                    reference: event.target.value,
                                    title: event.target.value,
                                  }))
                                }
                                style={styles.blockTitleInput}
                              />
                            </div>
                            <div style={styles.blockControls}>
                              <button
                                type="button"
                                style={{
                                  ...styles.liveSendButton,
                                  ...(activeLiveDisplayItemId === item.id ? styles.liveSendButtonActive : {}),
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleSendSermonBlockToLiveDisplay(item);
                                }}
                                disabled={!getEnabledSlideCount(item)}
                              >
                                <Smartphone size={14} />
                                {activeLiveDisplayItemId === item.id ? "Showing" : "Send live"}
                              </button>
                              <button
                                type="button"
                                aria-label={`Drag ${item.reference || item.title || "verse block"} to reorder`}
                                draggable
                                onClick={(event) => event.stopPropagation()}
                                onDragEnd={handleBlockDragEnd}
                                onDragStart={(event) => handleBlockDragStart(event, item.id)}
                                style={styles.dragHandleButton}
                                title="Drag to reorder"
                              >
                                <GripVertical size={16} />
                              </button>
                              <button
                                type="button"
                                style={styles.miniDangerButton}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeTimelineItem(item.id);
                                }}
                              >
                                Delete block
                              </button>
                            </div>
                          </div>
                          {item.warning ? <div style={styles.warningBox}>{item.warning}</div> : null}
                          <div style={styles.verseSlideList}>
                            {(item.slides || []).map((slide) => (
                              <label key={slide.id} style={styles.verseLine}>
                                <input
                                  type="checkbox"
                                  checked={slide.enabled !== false}
                                  onChange={(event) =>
                                    updateScriptureSlideEnabled(item.id, slide.id, event.target.checked)
                                  }
                                />
                                <span style={styles.verseLineNumber}>{slide.verseNumber}</span>
                                <span>{slide.text}</span>
                              </label>
                            ))}
                          </div>
                        </article>
                      );
                    }

                    if (item.type === "header") {
                      return (
                        <article
                          key={item.id}
                          onDragOver={(event) => handleBlockDragOver(event, item.id)}
                          onDrop={(event) => handleBlockDrop(event, item.id)}
                          style={{
                            ...styles.sermonBlock,
                            ...styles.headerBlock,
                            ...(isSelected ? styles.sermonBlockSelected : {}),
                            ...(draggedBlockId === item.id ? styles.sermonBlockDragging : {}),
                            ...(dragOverBlockId === item.id ? styles.sermonBlockDragOver : {}),
                          }}
                          onClick={() => setSelectedItemId(item.id)}
                        >
                          <div style={styles.blockHeader}>
                            <div style={styles.blockHeaderContent}>
                              <div style={styles.blockEyebrow}>Header block {index + 1}</div>
                              <input
                                value={item.title}
                                onChange={(event) =>
                                  updateTimelineItem(item.id, () => ({ title: event.target.value }))
                                }
                                onClick={(event) => event.stopPropagation()}
                                style={{
                                  ...styles.blockTitleInput,
                                  ...styles.headerTitleInput,
                                  ...getHeaderBlockTextStyle(item.title),
                                }}
                                placeholder="Header text"
                                autoCapitalize="words"
                              />
                            </div>
                            <div style={styles.blockControls}>
                              <button
                                type="button"
                                aria-label={`Drag ${item.title || "header block"} to reorder`}
                                draggable
                                onClick={(event) => event.stopPropagation()}
                                onDragEnd={handleBlockDragEnd}
                                onDragStart={(event) => handleBlockDragStart(event, item.id)}
                                style={styles.dragHandleButton}
                                title="Drag to reorder"
                              >
                                <GripVertical size={16} />
                              </button>
                              <button
                                type="button"
                                style={styles.miniDangerButton}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeTimelineItem(item.id);
                                }}
                              >
                                Delete block
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    }

                    if (item.type === "visual") {
                      return (
                        <article
                          key={item.id}
                          onDragOver={(event) => handleBlockDragOver(event, item.id)}
                          onDrop={(event) => handleBlockDrop(event, item.id)}
                          style={{
                            ...styles.sermonBlock,
                            ...styles.visualBlock,
                            ...(isSelected ? styles.sermonBlockSelected : {}),
                            ...(draggedBlockId === item.id ? styles.sermonBlockDragging : {}),
                            ...(dragOverBlockId === item.id ? styles.sermonBlockDragOver : {}),
                          }}
                          onClick={() => setSelectedItemId(item.id)}
                        >
                          <div style={styles.blockHeader}>
                            <div style={styles.blockHeaderContent}>
                              <div style={styles.blockEyebrow}>Visual slide {index + 1}</div>
                              <input
                                value={item.title}
                                onChange={(event) =>
                                  updateTimelineItem(item.id, () => ({ title: event.target.value }))
                                }
                                onClick={(event) => event.stopPropagation()}
                                style={styles.blockTitleInput}
                                placeholder="Visual slide title"
                                autoCapitalize="words"
                              />
                            </div>
                            <div style={styles.blockControls}>
                              <button
                                type="button"
                                style={{
                                  ...styles.liveSendButton,
                                  ...(activeLiveDisplayItemId === item.id ? styles.liveSendButtonActive : {}),
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleSendSermonBlockToLiveDisplay(item);
                                }}
                                disabled={!getEnabledSlideCount(item)}
                              >
                                <Smartphone size={14} />
                                {activeLiveDisplayItemId === item.id ? "Showing" : "Send live"}
                              </button>
                              <button
                                type="button"
                                aria-label={`Drag ${item.title || "visual slide"} to reorder`}
                                draggable
                                onClick={(event) => event.stopPropagation()}
                                onDragEnd={handleBlockDragEnd}
                                onDragStart={(event) => handleBlockDragStart(event, item.id)}
                                style={styles.dragHandleButton}
                                title="Drag to reorder"
                              >
                                <GripVertical size={16} />
                              </button>
                              <button
                                type="button"
                                style={styles.miniDangerButton}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeTimelineItem(item.id);
                                }}
                              >
                                Delete block
                              </button>
                            </div>
                          </div>
                          <div style={styles.visualSlideBody}>
                            <label style={styles.visualUploadButton} onClick={(event) => event.stopPropagation()}>
                              <ImageIcon size={16} />
                              Choose Image
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(event) => handleVisualSlideImageFile(item.id, event.target.files?.[0])}
                                style={styles.hiddenFileInput}
                              />
                            </label>
                            <input
                              value={item.imageUrl || ""}
                              onChange={(event) =>
                                updateTimelineItem(item.id, () => ({
                                  imageUrl: event.target.value,
                                  imagePath: "",
                                  storageBucket: "",
                                }))
                              }
                              onClick={(event) => event.stopPropagation()}
                              style={styles.visualUrlInput}
                              placeholder="Or paste an image URL"
                            />
                          </div>
                          {item.imageUrl ? (
                            <>
                              <div style={styles.visualSlidePreviewShell}>
                                <div style={styles.visualSlidePreviewStage}>
                                  <img
                                    src={item.imageUrl}
                                    alt={item.title || "Visual slide"}
                                    style={{
                                      ...styles.visualSlidePreviewImage,
                                      ...getVisualImageStyle(item),
                                    }}
                                  />
                                  {item.caption ? (
                                    <div style={styles.visualSlideCaptionPreview}>{item.caption}</div>
                                  ) : null}
                                </div>
                              </div>
                              <div style={styles.visualAdjustControls} onClick={(event) => event.stopPropagation()}>
                                <label style={styles.visualControlField}>
                                  <span>Fit</span>
                                  <select
                                    value={item.imageFit || "contain"}
                                    onChange={(event) =>
                                      updateTimelineItem(item.id, () => ({ imageFit: event.target.value }))
                                    }
                                    style={styles.visualControlSelect}
                                  >
                                    <option value="contain">Show all</option>
                                    <option value="cover">Fill slide</option>
                                  </select>
                                </label>
                                <label style={styles.visualControlField}>
                                  <span>Horizontal</span>
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={Number.isFinite(Number(item.imagePositionX)) ? Number(item.imagePositionX) : 50}
                                    onChange={(event) =>
                                      updateTimelineItem(item.id, () => ({ imagePositionX: Number(event.target.value) }))
                                    }
                                    style={styles.visualControlRange}
                                  />
                                </label>
                                <label style={styles.visualControlField}>
                                  <span>Vertical</span>
                                  <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={Number.isFinite(Number(item.imagePositionY)) ? Number(item.imagePositionY) : 50}
                                    onChange={(event) =>
                                      updateTimelineItem(item.id, () => ({ imagePositionY: Number(event.target.value) }))
                                    }
                                    style={styles.visualControlRange}
                                  />
                                </label>
                              </div>
                            </>
                          ) : (
                            <div style={styles.visualEmptyPreview}>
                              <ImageIcon size={20} />
                              <span>Add an image to send this slide live.</span>
                            </div>
                          )}
                        </article>
                      );
                    }

                    if (item.type === "text") {
                      return (
                        <article
                          key={item.id}
                          onDragOver={(event) => handleBlockDragOver(event, item.id)}
                          onDrop={(event) => handleBlockDrop(event, item.id)}
                          style={{
                            ...styles.sermonBlock,
                            ...styles.textSlideBlock,
                            ...(isSelected ? styles.sermonBlockSelected : {}),
                            ...(draggedBlockId === item.id ? styles.sermonBlockDragging : {}),
                            ...(dragOverBlockId === item.id ? styles.sermonBlockDragOver : {}),
                          }}
                          onClick={() => setSelectedItemId(item.id)}
                        >
                          <div style={styles.blockHeader}>
                            <div style={styles.blockHeaderContent}>
                              <div style={styles.blockEyebrow}>Text slide {index + 1}</div>
                              <input
                                value={item.title}
                                onChange={(event) =>
                                  updateTimelineItem(item.id, () => ({ title: event.target.value }))
                                }
                                onClick={(event) => event.stopPropagation()}
                                style={styles.blockTitleInput}
                                placeholder="Text slide title"
                                autoCapitalize="words"
                              />
                            </div>
                            <div style={styles.blockControls}>
                              <button
                                type="button"
                                style={{
                                  ...styles.liveSendButton,
                                  ...(activeLiveDisplayItemId === item.id ? styles.liveSendButtonActive : {}),
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleSendSermonBlockToLiveDisplay(item);
                                }}
                                disabled={!getEnabledSlideCount(item)}
                              >
                                <Smartphone size={14} />
                                {activeLiveDisplayItemId === item.id ? "Showing" : "Send live"}
                              </button>
                              <button
                                type="button"
                                aria-label={`Drag ${item.title || "text slide"} to reorder`}
                                draggable
                                onClick={(event) => event.stopPropagation()}
                                onDragEnd={handleBlockDragEnd}
                                onDragStart={(event) => handleBlockDragStart(event, item.id)}
                                style={styles.dragHandleButton}
                                title="Drag to reorder"
                              >
                                <GripVertical size={16} />
                              </button>
                              <button
                                type="button"
                                style={styles.miniDangerButton}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removeTimelineItem(item.id);
                                }}
                              >
                                Delete block
                              </button>
                            </div>
                          </div>
                          <div style={styles.noteToolbar} onClick={(event) => event.stopPropagation()}>
                            <button
                              type="button"
                              style={styles.noteToolbarButton}
                              title="Bold"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                applyNoteFormat(item.id, "bold");
                              }}
                            >
                              <Bold size={15} />
                            </button>
                            <button
                              type="button"
                              style={styles.noteToolbarButton}
                              title="Larger text"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                applyNoteFormat(item.id, "fontSize", "5");
                              }}
                            >
                              A+
                            </button>
                            <button
                              type="button"
                              style={styles.noteToolbarButton}
                              title="Bullets"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                applyNoteFormat(item.id, "insertUnorderedList");
                              }}
                            >
                              <List size={15} />
                            </button>
                            <button
                              type="button"
                              style={{
                                ...styles.noteToolbarButton,
                                ...styles.thoughtBubbleToolbarButton,
                              }}
                              title="Insert thought bubble"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                insertThoughtBubble(item.id);
                              }}
                            >
                              <MessageCircle size={15} />
                              Thought Bubble
                            </button>
                            <button
                              type="button"
                              style={styles.noteToolbarButton}
                              title="Outdent"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                applyNoteFormat(item.id, "outdent");
                              }}
                            >
                              <Outdent size={15} />
                            </button>
                            <button
                              type="button"
                              style={styles.noteToolbarButton}
                              title="Indent"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                applyNoteFormat(item.id, "indent");
                              }}
                            >
                              <Indent size={15} />
                            </button>
                          </div>
                          <NoteBlockEditor
                            itemId={item.id}
                            body={item.body}
                            isEmpty={!stripHtmlToText(item.body)}
                            onChange={(nextBody) => {
                              updateTimelineItem(item.id, () => ({ body: nextBody }));
                            }}
                          />
                        </article>
                      );
                    }

                    return (
                      <article
                        key={item.id}
                        onDragOver={(event) => handleBlockDragOver(event, item.id)}
                        onDrop={(event) => handleBlockDrop(event, item.id)}
                        style={{
                          ...styles.sermonBlock,
                          ...styles.noteBlock,
                          ...(isSelected ? styles.sermonBlockSelected : {}),
                          ...(draggedBlockId === item.id ? styles.sermonBlockDragging : {}),
                          ...(dragOverBlockId === item.id ? styles.sermonBlockDragOver : {}),
                        }}
                        onClick={() => setSelectedItemId(item.id)}
                      >
                        <div style={styles.blockHeader}>
                          <div>
                            <div style={styles.blockEyebrow}>Note block {index + 1}</div>
                            <input
                              value={item.title}
                              onChange={(event) =>
                                updateTimelineItem(item.id, () => ({ title: event.target.value }))
                              }
                              onClick={(event) => event.stopPropagation()}
                              style={styles.blockTitleInput}
                              placeholder="Note title"
                              autoCapitalize="words"
                            />
                          </div>
                          <div style={styles.blockControls}>
                            <button
                              type="button"
                              aria-label={`Drag ${item.title || "note block"} to reorder`}
                              draggable
                              onClick={(event) => event.stopPropagation()}
                              onDragEnd={handleBlockDragEnd}
                              onDragStart={(event) => handleBlockDragStart(event, item.id)}
                              style={styles.dragHandleButton}
                              title="Drag to reorder"
                            >
                              <GripVertical size={16} />
                            </button>
                            <button
                              type="button"
                              style={styles.miniDangerButton}
                              onClick={(event) => {
                                event.stopPropagation();
                                removeTimelineItem(item.id);
                              }}
                            >
                              Delete block
                            </button>
                          </div>
                        </div>
                        <div style={styles.noteToolbar} onClick={(event) => event.stopPropagation()}>
                          <button
                            type="button"
                            style={styles.noteToolbarButton}
                            title="Bold"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              applyNoteFormat(item.id, "bold");
                            }}
                          >
                            <Bold size={15} />
                          </button>
                          <button
                            type="button"
                            style={styles.noteToolbarButton}
                            title="Larger text"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              applyNoteFormat(item.id, "fontSize", "5");
                            }}
                          >
                            A+
                          </button>
                          <button
                            type="button"
                            style={styles.noteToolbarButton}
                            title="Bullets"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              applyNoteFormat(item.id, "insertUnorderedList");
                            }}
                          >
                            <List size={15} />
                          </button>
                          <button
                            type="button"
                            style={{
                              ...styles.noteToolbarButton,
                              ...styles.thoughtBubbleToolbarButton,
                            }}
                            title="Insert thought bubble"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              insertThoughtBubble(item.id);
                            }}
                          >
                            <MessageCircle size={15} />
                            Thought Bubble
                          </button>
                          <button
                            type="button"
                            style={styles.noteToolbarButton}
                            title="Outdent"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              applyNoteFormat(item.id, "outdent");
                            }}
                          >
                            <Outdent size={15} />
                          </button>
                          <button
                            type="button"
                            style={styles.noteToolbarButton}
                            title="Indent"
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              applyNoteFormat(item.id, "indent");
                            }}
                          >
                            <Indent size={15} />
                          </button>
                        </div>
                        <NoteBlockEditor
                          itemId={item.id}
                          body={item.body}
                          isEmpty={!stripHtmlToText(item.body)}
                          onChange={(nextBody) => {
                            updateTimelineItem(item.id, () => ({ body: nextBody }));
                          }}
                        />
                      </article>
                    );
                  }) : (
                    <div style={styles.emptyEditorBlock}>
                      <div style={styles.emptyEditorIcon}>
                        <BookOpen size={22} />
                      </div>
                      <div>
                        <strong style={styles.emptyEditorTitle}>Start your sermon flow</strong>
                        <span style={styles.emptyEditorText}>
                          Add a verse for slides or a note block for preacher-only iPad notes.
                        </span>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </section>
          </div>
        </section>
      </main>
    );
  }

  if (viewMode === "live") {
    return (
      <main style={styles.readerPage}>
        <GlobalNotificationPill
          message={liveDisplayStatus}
          onDismiss={() => setLiveDisplayStatus("")}
          tone={liveDisplayStatusIsError ? "error" : "success"}
        />

        <header style={{ ...styles.readerTopbar, ...(isPhone ? styles.readerTopbarPhone : {}) }}>
          <button
            type="button"
            style={styles.readerGhostButton}
            onClick={draftBeforeSavedLiveView ? openDashboard : handleCloseBuilder}
          >
            <X size={17} />
            Close
          </button>
          <div style={styles.readerTitleBlock}>
            <div style={styles.readerKicker}>iPad sermon viewer</div>
            <h1 style={styles.readerTitle}>{getDisplayTitle(draft)}</h1>
            <p style={styles.readerMeta}>
              {draft.speakerName || "No speaker"} • {formatSermonDate(draft.sermonDate)}
            </p>
          </div>
          <button type="button" style={styles.readerGhostButton} onClick={handleReturnLiveDisplayToLoop}>
            <RotateCcw size={17} />
            Return to Loop
          </button>
        </header>

        <section style={styles.readerShell}>
          <article style={styles.readerDocument}>
            <section
              style={{
                ...styles.readerBlock,
                ...(selectedItemId === "title-slide" ? styles.readerBlockActive : {}),
              }}
              onClick={() => setSelectedItemId("title-slide")}
            >
              <div style={styles.readerBlockHeader}>
                <div>
                  <div style={styles.readerBlockKicker}>Opening</div>
                  <h2 style={styles.readerHeadline}>{getDisplayTitle(draft)}</h2>
                  <p style={styles.readerSubhead}>
                    {draft.speakerName || "No speaker"} • {formatSermonDate(draft.sermonDate)}
                  </p>
                </div>
                <button
                  type="button"
                  style={{
                    ...styles.readerLiveButton,
                    ...(activeLiveDisplayItemId === "title-slide" ? styles.readerLiveButtonActive : {}),
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSendSermonBlockToLiveDisplay(buildTitleSlide(draft));
                  }}
                >
                  <Smartphone size={17} />
                  {activeLiveDisplayItemId === "title-slide" ? "On Live Display" : "Send to Live Display"}
                </button>
              </div>
            </section>

            {draft.items.map((item, index) => {
              const isActive = selectedItemId === item.id;

              if (item.type === "scripture") {
                const enabledSlides = (item.slides || []).filter((slide) => slide.enabled !== false);

                return (
                  <section
                    key={item.id}
                    style={{
                      ...styles.readerBlock,
                      ...(isActive ? styles.readerBlockActive : {}),
                    }}
                    onClick={() => setSelectedItemId(item.id)}
                  >
                    <div style={styles.readerBlockHeader}>
                      <div>
                        <div style={styles.readerBlockKicker}>Verse {index + 1}</div>
                        <h2 style={styles.readerScriptureTitle}>{item.reference || item.title || "Scripture"}</h2>
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.readerLiveButton,
                          ...(activeLiveDisplayItemId === item.id ? styles.readerLiveButtonActive : {}),
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSendSermonBlockToLiveDisplay(item);
                        }}
                        disabled={!enabledSlides.length}
                      >
                        <Smartphone size={17} />
                        {activeLiveDisplayItemId === item.id ? "On Live Display" : "Send to Live Display"}
                      </button>
                    </div>

                    <div style={styles.readerVerseList}>
                      {(item.slides || []).map((slide) => (
                        <div
                          key={slide.id}
                          style={{
                            ...styles.readerVerse,
                            ...(slide.enabled === false ? styles.readerVerseDisabled : {}),
                          }}
                        >
                          {slide.verseNumber ? (
                            <span style={styles.readerVerseNumber}>{slide.verseNumber}</span>
                          ) : null}
                          <span>{slide.text}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              if (item.type === "header") {
                return (
                  <section
                    key={item.id}
                    style={{
                      ...styles.readerHeaderBlock,
                      ...(isActive ? styles.readerBlockActive : {}),
                    }}
                    onClick={() => setSelectedItemId(item.id)}
                  >
                    <div style={styles.readerBlockKicker}>Header {index + 1}</div>
                    <h2
                      style={{
                        ...styles.readerHeaderTitle,
                        ...getHeaderBlockTextStyle(item.title),
                      }}
                    >
                      {item.title || "Section Header"}
                    </h2>
                  </section>
                );
              }

              if (item.type === "visual") {
                return (
                  <section
                    key={item.id}
                    style={{
                      ...styles.readerBlock,
                      ...(isActive ? styles.readerBlockActive : {}),
                    }}
                    onClick={() => setSelectedItemId(item.id)}
                  >
                    <div style={styles.readerBlockHeader}>
                      <div>
                        <div style={styles.readerBlockKicker}>Visual {index + 1}</div>
                        <h2 style={styles.readerNoteTitle}>{item.title || "Visual Slide"}</h2>
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.readerLiveButton,
                          ...(activeLiveDisplayItemId === item.id ? styles.readerLiveButtonActive : {}),
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSendSermonBlockToLiveDisplay(item);
                        }}
                        disabled={!getEnabledSlideCount(item)}
                      >
                        <Smartphone size={17} />
                        {activeLiveDisplayItemId === item.id ? "On Live Display" : "Send to Live Display"}
                      </button>
                    </div>
                    {item.imageUrl ? (
                      <div style={styles.visualPreviewFrame}>
                        <img
                          src={item.imageUrl}
                          alt={item.title || "Visual slide"}
                          style={{
                            ...styles.visualPreviewImage,
                            ...getVisualImageStyle(item),
                          }}
                        />
                      </div>
                    ) : (
                      <p style={styles.readerNoteBody}>No image selected yet.</p>
                    )}
                    {item.caption ? <p style={styles.readerNoteBody}>{item.caption}</p> : null}
                  </section>
                );
              }

              if (item.type === "text") {
                return (
                  <section
                    key={item.id}
                    style={{
                      ...styles.readerBlock,
                      ...(isActive ? styles.readerBlockActive : {}),
                    }}
                    onClick={() => setSelectedItemId(item.id)}
                  >
                    <div style={styles.readerBlockHeader}>
                      <div>
                        <div style={styles.readerBlockKicker}>Text slide {index + 1}</div>
                        <h2 style={styles.readerNoteTitle}>{item.title || "Text Slide"}</h2>
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.readerLiveButton,
                          ...(activeLiveDisplayItemId === item.id ? styles.readerLiveButtonActive : {}),
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSendSermonBlockToLiveDisplay(item);
                        }}
                        disabled={!getEnabledSlideCount(item)}
                      >
                        <Smartphone size={17} />
                        {activeLiveDisplayItemId === item.id ? "On Live Display" : "Send to Live Display"}
                      </button>
                    </div>
                    {stripHtmlToText(item.body) ? (
                      <div
                        style={styles.readerTextSlideBody}
                        dangerouslySetInnerHTML={{ __html: item.body }}
                      />
                    ) : (
                      <p style={styles.readerNoteBody}>No text yet.</p>
                    )}
                  </section>
                );
              }

              return (
                <section
                  key={item.id}
                  style={{
                    ...styles.readerBlock,
                    ...(isActive ? styles.readerBlockActive : {}),
                  }}
                  onClick={() => setSelectedItemId(item.id)}
                >
                  <div style={styles.readerBlockKicker}>Note {index + 1}</div>
                  <h2 style={styles.readerNoteTitle}>{item.title || "Note Block"}</h2>
                  {stripHtmlToText(item.body) ? (
                    <div
                      style={styles.readerNoteBody}
                      dangerouslySetInnerHTML={{ __html: item.body }}
                    />
                  ) : (
                    <p style={styles.readerNoteBody}>No note text yet.</p>
                  )}
                </section>
              );
            })}

          </article>
        </section>

        <nav style={styles.readerBottomActions} aria-label="Live Display return actions">
          <button
            type="button"
            style={{ ...styles.readerBottomButton, ...styles.readerBottomButtonMuted }}
            onClick={handleReturnLiveDisplayToLoop}
          >
            <RotateCcw size={18} />
            Return to Pre-Service Loop
          </button>
          <button
            type="button"
            style={styles.readerBottomButton}
            onClick={handleReturnLiveDisplayToServiceSlides}
          >
            <List size={18} />
            Return to Service Slides
          </button>
        </nav>
      </main>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ ...styles.topBar, ...(isPhone ? styles.topBarPhone : {}) }}>
        <div>
          <div style={styles.heading}>
            {viewMode === "dashboard"
              ? "Sermons"
              : viewMode === "live"
                ? "iPad Viewer"
                : "Sermon Builder"}
          </div>
          <div style={styles.saveState}>
            {viewMode === "dashboard"
              ? "Build and manage your sermon library"
              : saveState}
          </div>
        </div>

        <div style={{ ...styles.topActions, ...(isPhone ? styles.topActionsPhone : {}) }}>
          {viewMode !== "dashboard" ? (
            <button
              style={styles.secondaryAction}
              onClick={openDashboard}
            >
              Back to Sermons
            </button>
          ) : null}

          {viewMode === "builder" ? (
            <>
              <button
                style={styles.secondaryAction}
                onClick={() => handlePushToService(draft)}
                disabled={Boolean(pushState)}
              >
                {pushState || "Add to Service"}
              </button>
              <button
                style={styles.primaryAction}
                onClick={() => handleOpenLiveView(draft)}
              >
                iPad Viewer
              </button>
            </>
          ) : null}

          {viewMode === "live" ? (
            <>
              <button
                style={styles.secondaryAction}
                onClick={openBuilder}
              >
                Back to Builder
              </button>
              <button
                style={styles.primaryAction}
                onClick={() => handlePushToService(draft)}
                disabled={Boolean(pushState)}
              >
                {pushState || "Send"}
              </button>
            </>
          ) : null}

          {viewMode === "dashboard" ? (
            <>
              <button
                style={styles.secondaryAction}
                onClick={refreshPastSermons}
              >
                {pastLoading ? "Refreshing..." : "Refresh"}
              </button>
              <button style={styles.primaryAction} onClick={handleNewSermon}>
                Sermon Builder
              </button>
            </>
          ) : null}
        </div>
      </div>

      {statusMessage ? <div style={styles.statusBanner}>{statusMessage}</div> : null}

      {viewMode === "dashboard" ? (
        <div style={styles.dashboardLayout}>
          <div style={{ ...styles.dashboardHero, ...(isPhone ? styles.dashboardHeroPhone : {}) }}>
            <div>
              <div style={styles.dashboardTitle}>Your Sermon Dashboard</div>
              <div style={styles.dashboardText}>
                Start a new sermon, reopen an old one, or jump into live view
                with your notes ready for the preacher.
              </div>
            </div>

            <div style={{ ...styles.dashboardActions, ...(isPhone ? styles.dashboardActionsPhone : {}) }}>
              <button style={styles.primaryAction} onClick={handleNewSermon}>
                Start New Sermon
              </button>
            </div>
          </div>

          <div style={styles.librarySection}>
            <div style={styles.sectionTitle}>All Sermons</div>

            {sermonLibrary.length === 0 ? (
              <div style={styles.emptyState}>
                No sermons yet. Start your first sermon in Sermon Builder.
              </div>
            ) : (
              <div style={styles.sermonTable}>
                {!isPhone ? (
                  <div style={{ ...styles.sermonRow, ...styles.sermonHeaderRow }}>
                    <div>Sermon</div>
                    <div>Speaker</div>
                    <div>Date</div>
                    <div>Slides</div>
                    <div style={styles.sermonActionsHeader}>Actions</div>
                  </div>
                ) : null}

                {sermonLibrary.map((sermon) => (
                  <div
                    key={`saved-${sermon.id || sermon.archiveId}`}
                    style={{
                      ...styles.sermonRow,
                      ...(isPhone ? styles.sermonRowPhone : {}),
                    }}
                  >
                    <div style={styles.sermonTitleCell}>
                      <div style={styles.sermonCardTitle}>
                        {getDisplayTitle(sermon)}
                      </div>
                      <span
                        style={{
                          ...styles.sermonBadge,
                          ...styles.savedBadge,
                        }}
                      >
                        Saved Sermon
                      </span>
                    </div>

                    <div style={styles.sermonCell}>
                      {sermon.speakerName || "No speaker"}
                    </div>
                    <div style={styles.sermonCell}>
                      {sermon.sermonDate || "No date"}
                    </div>
                    <div style={styles.sermonCell}>
                      {(sermon.items || []).length} item
                      {(sermon.items || []).length === 1 ? "" : "s"} in sermon
                    </div>

                    <div style={styles.sermonCardActions}>
                      <button
                        style={styles.tableActionButton}
                        onClick={() => handleEditSermon(sermon)}
                      >
                        Edit
                      </button>
                      <button
                        style={styles.tableActionButton}
                        onClick={() => handleOpenLiveView(sermon)}
                      >
                        iPad Viewer
                      </button>
                      <button
                        style={styles.tablePrimaryButton}
                        onClick={() => handlePushToService(sermon)}
                        disabled={Boolean(pushState)}
                      >
                        Send
                      </button>
                      <button
                        style={styles.tableDeleteButton}
                        onClick={() => handleDeleteSermon(sermon)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {viewMode === "builder" ? (
        <>
          <div style={{ ...styles.setupGrid, ...(isPhone ? styles.setupGridPhone : {}) }}>
            <div style={styles.setupCard}>
              <label style={styles.label}>Sermon Title</label>
              <input
                value={draft.title}
                onChange={(event) => updateTitleField("title", event.target.value)}
                style={styles.input}
                placeholder="Resurrection and Renewal"
              />
            </div>

            <div style={styles.setupCard}>
              <label style={styles.label}>Speaker Name</label>
              <input
                value={draft.speakerName}
                onChange={(event) =>
                  updateTitleField("speakerName", event.target.value)
                }
                style={styles.input}
                placeholder="Pastor Smith"
              />
            </div>

            <div style={styles.setupCard}>
              <label style={styles.label}>Date</label>
              <input
                type="date"
                value={draft.sermonDate}
                onChange={(event) =>
                  updateTitleField("sermonDate", event.target.value)
                }
                style={styles.input}
              />
            </div>
          </div>

          <div style={{ ...styles.builderLayout, ...(isPhone ? styles.builderLayoutPhone : {}) }}>
            <div style={styles.sidebar}>
              <div style={styles.sidebarCard}>
                <div style={styles.sidebarTitle}>Add Content</div>
                <button
                  style={styles.primaryWideButton}
                  onClick={() => setIsScriptureModalOpen(true)}
                  disabled={isAddingScripture}
                >
                  {isAddingScripture ? "Adding Scripture..." : "Add Scripture Block"}
                </button>

                <button
                  style={styles.secondaryWideButton}
                  onClick={handleAddNoteBlockLegacy}
                >
                  Add Note Block
                </button>
              </div>

              <div style={styles.sidebarCard}>
                <div style={styles.sidebarTitle}>Sermon Timeline</div>
                {timelineItems.map((item, index) => {
                  const isSelected = selectedItemId === item.id;
                  const isTitle = item.id === "title-slide";
                  const itemLabel =
                    item.type === "scripture"
                      ? item.reference
                      : item.type === "custom"
                        ? item.title || "Note Block"
                        : "Title Slide";

                  return (
                    <div
                      key={item.id}
                      onClick={() => setSelectedItemId(item.id)}
                      style={{
                        ...styles.timelineItem,
                        ...(isSelected ? styles.timelineItemActive : {}),
                      }}
                    >
                      <div>
                        <div style={styles.timelineIndex}>{index + 1}</div>
                        <div style={styles.timelineLabel}>{itemLabel}</div>
                        <div style={styles.timelineMeta}>
                          {item.type === "scripture"
                            ? `${getEnabledSlideCount(item)} of ${item.slides?.length || 0} slide${
                                item.slides?.length === 1 ? "" : "s"
                              }`
                            : getEnabledSlideCount(item) === 0
                              ? item.type === "custom"
                                ? "iPad notes only"
                                : "Hidden from slideshow"
                              : "Header"}
                        </div>
                      </div>

                      {!isTitle ? (
                        <div style={styles.timelineActions}>
                          <button
                            style={styles.reorderButton}
                            onClick={(event) => {
                              event.stopPropagation();
                              reorderItems(item.id, "up");
                            }}
                          >
                            Up
                          </button>
                          <button
                            style={styles.reorderButton}
                            onClick={(event) => {
                              event.stopPropagation();
                              reorderItems(item.id, "down");
                            }}
                          >
                            Down
                          </button>
                          <button
                            style={styles.removeButton}
                            onClick={(event) => {
                              event.stopPropagation();
                              removeTimelineItem(item.id);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={styles.editorPanel}>
              {selectedItem?.type === "title" ? (
                <div style={styles.editorCard}>
                  <div style={styles.editorHeaderRow}>
                    <div style={styles.editorTitle}>Title Slide</div>
                    <label style={styles.toggleRow}>
                      <span style={styles.toggleLabel}>Add to Slideshow</span>
                      <input
                        type="checkbox"
                        checked={draft.titleSlideEnabled !== false}
                        onChange={(event) =>
                          updateTitleSlideEnabled(event.target.checked)
                        }
                      />
                    </label>
                  </div>
                  <div style={styles.previewCard}>
                    <div style={styles.previewHeadline}>
                      {draft.title || "Untitled Sermon"}
                    </div>
                    <div style={styles.previewSubline}>
                      {draft.speakerName || "Speaker name"}
                    </div>
                    <div style={styles.previewDate}>{draft.sermonDate}</div>
                  </div>
                </div>
              ) : null}

              {selectedItem?.type === "scripture" ? (
                <div style={styles.editorCard}>
                  <div style={styles.editorTitle}>Scripture Block</div>

                  <label style={styles.label}>Reference</label>
                  <input
                    value={selectedItem.reference}
                    onChange={(event) =>
                      updateTimelineItem(selectedItem.id, () => ({
                        reference: event.target.value,
                        title: event.target.value,
                      }))
                    }
                    style={styles.input}
                  />

                  <label style={styles.label}>Translation</label>
                  <select
                    value={selectedItem.translationKey}
                    onChange={(event) =>
                      updateTimelineItem(selectedItem.id, () => ({
                        translationKey: event.target.value,
                      }))
                    }
                    style={styles.input}
                  >
                    {["NKJV", "KJV", "NASB"].map((translationKey) => (
                      <option key={translationKey} value={translationKey}>
                        {translationKey}
                      </option>
                    ))}
                  </select>

                  {selectedItem.warning ? (
                    <div style={styles.warningBox}>{selectedItem.warning}</div>
                  ) : null}

                  <div style={styles.editorTitleSmall}>Generated Slides</div>
                  <div style={styles.slidePreviewList}>
                    {(selectedItem.slides || []).map((slide) => (
                      <div key={slide.id} style={styles.slideCard}>
                        <div style={styles.slideCardHeader}>
                          <div style={styles.slideReference}>{slide.title}</div>
                          <label style={styles.toggleRow}>
                            <span style={styles.toggleLabel}>Add to Slideshow</span>
                            <input
                              type="checkbox"
                              checked={slide.enabled !== false}
                              onChange={(event) =>
                                updateScriptureSlideEnabled(
                                  selectedItem.id,
                                  slide.id,
                                  event.target.checked
                                )
                              }
                            />
                          </label>
                        </div>
                        <div style={styles.slideText}>
                          {slide.verseNumber ? (
                            <span style={styles.slideVerseNumber}>
                              {slide.verseNumber}
                            </span>
                          ) : null}
                          <span>{slide.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {selectedItem?.type === "custom" ? (
                <div style={styles.editorCard}>
                  <div style={styles.editorHeaderRow}>
                    <div style={styles.editorTitle}>Note Block</div>
                  </div>

                  <label style={styles.label}>Note Title</label>
                  <input
                    value={selectedItem.title}
                    onChange={(event) =>
                      updateTimelineItem(selectedItem.id, () => ({
                        title: event.target.value,
                      }))
                    }
                    style={styles.input}
                    placeholder="Main Point"
                  />

                  <label style={styles.label}>Preacher Notes</label>
                  <textarea
                    value={selectedItem.body}
                    onChange={(event) =>
                      updateTimelineItem(selectedItem.id, () => ({
                        body: event.target.value,
                      }))
                    }
                    style={styles.largeTextarea}
                    placeholder="Bullet points, key ideas, section header..."
                  />
                </div>
              ) : null}

              {selectedItem?.type === "visual" ? (
                <div style={styles.editorCard}>
                  <div style={styles.editorHeaderRow}>
                    <div style={styles.editorTitle}>Visual Slide</div>
                  </div>

                  <label style={styles.label}>Slide Title</label>
                  <input
                    value={selectedItem.title}
                    onChange={(event) =>
                      updateTimelineItem(selectedItem.id, () => ({
                        title: event.target.value,
                      }))
                    }
                    style={styles.input}
                    placeholder="Visual Slide"
                  />

                  <label style={styles.label}>Image</label>
                  <label style={styles.visualUploadButton}>
                    <ImageIcon size={16} />
                    Choose Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => handleVisualSlideImageFile(selectedItem.id, event.target.files?.[0])}
                      style={styles.hiddenFileInput}
                    />
                  </label>

                  <label style={styles.label}>Image URL</label>
                  <input
                    value={selectedItem.imageUrl || ""}
                    onChange={(event) =>
                      updateTimelineItem(selectedItem.id, () => ({
                        imageUrl: event.target.value,
                        imagePath: "",
                        storageBucket: "",
                      }))
                    }
                    style={styles.input}
                    placeholder="https://..."
                  />

                  <label style={styles.label}>Caption</label>
                  <textarea
                    value={selectedItem.caption || ""}
                    onChange={(event) =>
                      updateTimelineItem(selectedItem.id, () => ({
                        caption: event.target.value,
                      }))
                    }
                    style={styles.largeTextarea}
                    placeholder="Optional caption for the live display."
                  />

                  {selectedItem.imageUrl ? (
                    <>
                      <div style={styles.visualSlidePreviewShell}>
                        <div style={styles.visualSlidePreviewStage}>
                          <img
                            src={selectedItem.imageUrl}
                            alt={selectedItem.title || "Visual slide"}
                            style={{
                              ...styles.visualSlidePreviewImage,
                              ...getVisualImageStyle(selectedItem),
                            }}
                          />
                          {selectedItem.caption ? (
                            <div style={styles.visualSlideCaptionPreview}>{selectedItem.caption}</div>
                          ) : null}
                        </div>
                      </div>
                      <div style={styles.visualAdjustControls}>
                        <label style={styles.visualControlField}>
                          <span>Fit</span>
                          <select
                            value={selectedItem.imageFit || "contain"}
                            onChange={(event) =>
                              updateTimelineItem(selectedItem.id, () => ({ imageFit: event.target.value }))
                            }
                            style={styles.visualControlSelect}
                          >
                            <option value="contain">Show all</option>
                            <option value="cover">Fill slide</option>
                          </select>
                        </label>
                        <label style={styles.visualControlField}>
                          <span>Horizontal</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={Number.isFinite(Number(selectedItem.imagePositionX)) ? Number(selectedItem.imagePositionX) : 50}
                            onChange={(event) =>
                              updateTimelineItem(selectedItem.id, () => ({ imagePositionX: Number(event.target.value) }))
                            }
                            style={styles.visualControlRange}
                          />
                        </label>
                        <label style={styles.visualControlField}>
                          <span>Vertical</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={Number.isFinite(Number(selectedItem.imagePositionY)) ? Number(selectedItem.imagePositionY) : 50}
                            onChange={(event) =>
                              updateTimelineItem(selectedItem.id, () => ({ imagePositionY: Number(event.target.value) }))
                            }
                            style={styles.visualControlRange}
                          />
                        </label>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}

            </div>
          </div>

          {renderScriptureModal()}
        </>
      ) : null}

      {viewMode === "live" ? (
        <div style={{ ...styles.liveLayout, ...(isPhone ? styles.liveLayoutPhone : {}) }}>
          <div style={styles.liveSidebar}>
            <div style={styles.sidebarCard}>
              <div style={styles.sidebarTitle}>iPad Sermon Flow</div>
              {timelineItems
                .filter((item) => item.type === "custom" || item.type === "visual" || getEnabledSlideCount(item) > 0)
                .map((item, index) => {
                  const isSelected = selectedItemId === item.id;
                  const itemLabel =
                    item.type === "scripture"
                      ? item.reference
                      : item.type === "custom"
                        ? item.title || "Note Block"
                        : item.type === "visual"
                          ? item.title || "Visual Slide"
                        : "Title Slide";

                  return (
                    <button
                      key={item.id}
                      onClick={() => setSelectedItemId(item.id)}
                      style={{
                        ...styles.liveTimelineItem,
                        ...(isSelected ? styles.liveTimelineItemActive : {}),
                      }}
                    >
                      <span style={styles.liveTimelineIndex}>{index + 1}</span>
                      <span style={styles.liveTimelineLabel}>{itemLabel}</span>
                    </button>
                  );
                })}
            </div>
          </div>

          <div style={styles.liveContent}>
            <div style={styles.liveStage}>
              <div style={styles.editorTitle}>
                {selectedItem?.type === "scripture"
                  ? selectedItem.reference
                  : selectedItem?.type === "custom"
                    ? selectedItem.title || "Note Block"
                    : selectedItem?.type === "visual"
                      ? selectedItem.title || "Visual Slide"
                    : getDisplayTitle(draft)}
              </div>

              {selectedItem?.type === "title" ? (
                <div style={styles.liveTitleCard}>
                  <div style={styles.liveTitleHeadline}>{getDisplayTitle(draft)}</div>
                  <div style={styles.liveTitleMeta}>{draft.speakerName}</div>
                  <div style={styles.liveTitleDate}>{draft.sermonDate}</div>
                </div>
              ) : null}

              {selectedItem?.type === "scripture" ? (
                <div style={styles.liveSlidesList}>
                  {(selectedItem.slides || [])
                    .filter((slide) => slide.enabled !== false)
                    .map((slide) => (
                      <div key={slide.id} style={styles.liveSlideCard}>
                        <div style={styles.liveSlideReference}>{slide.title}</div>
                        <div style={styles.liveSlideText}>
                          {slide.verseNumber ? (
                            <span style={styles.liveVerseNumber}>
                              {slide.verseNumber}
                            </span>
                          ) : null}
                          <span>{slide.text}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : null}

              {selectedItem?.type === "custom" ? (
                <div style={styles.liveCustomCard}>
                  <div style={styles.liveCustomTitle}>{selectedItem.title}</div>
                  <div style={styles.liveCustomBody}>{selectedItem.body}</div>
                </div>
              ) : null}

              {selectedItem?.type === "visual" ? (
                <div style={styles.liveCustomCard}>
                  {selectedItem.imageUrl ? (
                    <img
                      src={selectedItem.imageUrl}
                      alt={selectedItem.title || "Visual slide"}
                      style={{
                        ...styles.visualPreviewImage,
                        ...getVisualImageStyle(selectedItem),
                      }}
                    />
                  ) : (
                    <div style={styles.visualEmptyPreview}>
                      <ImageIcon size={20} />
                      <span>No image selected yet.</span>
                    </div>
                  )}
                  {selectedItem.caption ? <div style={styles.liveCustomBody}>{selectedItem.caption}</div> : null}
                </div>
              ) : null}
            </div>

          </div>
        </div>
      ) : null}
    </div>
  );
}

const styles = {
  launchPage: {
    background: "linear-gradient(180deg, #f7f9ff 0%, #eef2f8 48%, #e9eef7 100%)",
    color: "#172033",
    minHeight: "100dvh",
    padding: "16px clamp(16px, 3vw, 34px) 40px",
  },
  launchHeader: {
    alignContent: "space-between",
    background: "#23345d",
    border: "1px solid rgba(255,255,255,0.58)",
    borderRadius: 8,
    boxShadow: "0 18px 42px rgba(23,32,51,0.14)",
    display: "grid",
    gap: 28,
    gridTemplateRows: "auto 1fr",
    margin: "0 auto",
    maxWidth: 1180,
    minHeight: 200,
    overflow: "hidden",
    padding: "22px clamp(16px, 3vw, 28px) 14px",
    position: "relative",
  },
  launchHeaderBackdrop: {
    height: "100%",
    inset: 0,
    objectFit: "cover",
    objectPosition: "center",
    position: "absolute",
    width: "100%",
    zIndex: 0,
  },
  launchHeaderOverlay: {
    background: "linear-gradient(90deg, rgba(12,18,32,0.62), rgba(31,68,139,0.22))",
    inset: 0,
    position: "absolute",
    zIndex: 1,
  },
  launchHeaderTop: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 18,
    justifyContent: "space-between",
    position: "relative",
    zIndex: 2,
  },
  launchHeaderBrand: {
    alignItems: "center",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "72px minmax(0, 1fr)",
    minWidth: 0,
  },
  launchHeaderIconWrap: {
    alignItems: "center",
    background: "rgba(255,255,255,0.9)",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 8,
    boxShadow: "0 12px 28px rgba(12,18,32,0.16)",
    display: "inline-flex",
    flex: "0 0 72px",
    height: 72,
    justifyContent: "center",
    width: 72,
  },
  launchHeaderIcon: {
    height: 54,
    objectFit: "contain",
    width: 54,
  },
  launchHeaderTitleStack: {
    display: "grid",
    gap: 5,
    minWidth: 0,
  },
  launchHeaderTitle: {
    color: "#ffffff",
    fontSize: 34,
    fontWeight: 900,
    lineHeight: 1,
    textShadow: "0 2px 10px rgba(12,18,32,0.34)",
  },
  launchHeaderOrg: {
    color: "rgba(255,255,255,0.88)",
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.1,
    textShadow: "0 2px 8px rgba(12,18,32,0.26)",
  },
  launchHeaderTools: {
    alignItems: "center",
    alignSelf: "end",
    display: "grid",
    gap: 12,
    gridTemplateColumns: "minmax(0, 3fr) minmax(150px, auto)",
    marginTop: 0,
    position: "relative",
    transform: "translateY(4px)",
    zIndex: 2,
  },
  launchUserStack: {
    alignItems: "stretch",
    display: "grid",
    gap: 8,
    justifyItems: "end",
  },
  launchUserTile: {
    alignItems: "center",
    background: "rgba(255,255,255,0.9)",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 999,
    boxShadow: "0 12px 28px rgba(23,32,51,0.08)",
    display: "flex",
    gap: 10,
    padding: "8px 14px 8px 8px",
  },
  launchUserCopy: {
    display: "grid",
    gap: 2,
    minWidth: 0,
  },
  avatar: {
    alignItems: "center",
    background: SERMON_BRAND,
    borderRadius: 999,
    color: "#ffffff",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    height: 34,
    justifyContent: "center",
    width: 34,
  },
  launchSearchBox: {
    alignItems: "center",
    backdropFilter: "blur(16px) saturate(1.15)",
    WebkitBackdropFilter: "blur(16px) saturate(1.15)",
    background: "rgba(255,255,255,0.88)",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 999,
    boxShadow: "0 12px 28px rgba(12,18,32,0.14)",
    color: "#344054",
    display: "flex",
    gap: 8,
    minHeight: 48,
    padding: "0 14px",
  },
  launchSearchInput: {
    background: "transparent",
    border: 0,
    color: "#172033",
    flex: 1,
    font: "inherit",
    minWidth: 0,
    outline: 0,
  },
  launchAppsButton: {
    alignItems: "center",
    backdropFilter: "blur(16px) saturate(1.15)",
    WebkitBackdropFilter: "blur(16px) saturate(1.15)",
    background: "rgba(255,255,255,0.88)",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 999,
    boxShadow: "0 12px 28px rgba(12,18,32,0.14)",
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 38,
    padding: "0 14px",
    whiteSpace: "nowrap",
  },
  launchNewButton: {
    alignItems: "center",
    backdropFilter: "blur(16px) saturate(1.15)",
    WebkitBackdropFilter: "blur(16px) saturate(1.15)",
    background: "linear-gradient(135deg, rgba(255,255,255,0.92), rgba(255,255,255,0.7))",
    border: "1px solid rgba(255,255,255,0.76)",
    borderRadius: 999,
    boxShadow: "0 12px 28px rgba(12,18,32,0.14), inset 0 1px 0 rgba(255,255,255,0.56)",
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 9,
    justifyContent: "center",
    minHeight: 48,
    justifySelf: "end",
    padding: "0 14px",
    whiteSpace: "nowrap",
  },
  launchNewButtonIcon: {
    height: 28,
    objectFit: "contain",
    width: 28,
  },
  launchNotice: {
    background: "#dcfce7",
    borderRadius: 8,
    color: "#166534",
    fontWeight: 800,
    margin: "14px auto 0",
    maxWidth: 1180,
    padding: "10px 14px",
  },
  launchContent: {
    display: "grid",
    gap: 24,
    margin: "24px auto 0",
    maxWidth: 1180,
  },
  launchSection: {
    display: "grid",
    gap: 12,
  },
  launchSectionHeader: {
    alignItems: "center",
    display: "flex",
    gap: 10,
    justifyContent: "flex-start",
  },
  sectionPill: {
    alignItems: "center",
    background: "rgba(255,255,255,0.94)",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    boxShadow: "0 12px 28px rgba(23,32,51,0.08)",
    color: "#172033",
    display: "inline-flex",
    gap: 10,
    justifyContent: "space-between",
    minHeight: 48,
    padding: "0 8px 0 18px",
    width: "100%",
  },
  sectionCount: {
    alignItems: "center",
    background: "#eef3ff",
    border: "1px solid #dbe5ff",
    borderRadius: 999,
    color: SERMON_BRAND,
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    height: 28,
    justifyContent: "center",
    minWidth: 28,
    padding: "0 10px",
  },
  recentGrid: {
    display: "grid",
    gap: 18,
    gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
  },
  recentCard: {
    appearance: "none",
    WebkitAppearance: "none",
    background: "#ffffff",
    border: "1px solid transparent",
    borderRadius: 18,
    boxShadow: "0 12px 26px rgba(23,32,51,0.08)",
    color: "#172033",
    cursor: "pointer",
    display: "grid",
    gap: 16,
    gridTemplateRows: "auto minmax(54px, auto) 1fr",
    minHeight: 230,
    outline: "none",
    padding: 20,
    textAlign: "left",
    transition: "box-shadow 160ms ease, transform 160ms ease",
  },
  recentCardHover: {
    borderColor: "transparent",
    boxShadow: "0 18px 42px rgba(23,32,51,0.14)",
    transform: "translateY(-2px)",
  },
  createSermonCard: {
    alignItems: "center",
    alignContent: "center",
    backdropFilter: "blur(18px) saturate(1.2)",
    WebkitBackdropFilter: "blur(18px) saturate(1.2)",
    background: "linear-gradient(135deg, rgba(255,255,255,0.38), rgba(248,250,252,0.18))",
    border: "1px solid transparent",
    boxShadow: "0 10px 28px rgba(23,32,51,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
    color: "#475467",
    gap: 14,
    gridTemplateRows: "auto auto",
    justifyItems: "center",
    placeContent: "center",
    textAlign: "center",
  },
  createSermonCardHover: {
    background: "linear-gradient(135deg, rgba(255,255,255,0.54), rgba(248,250,252,0.28))",
    borderColor: "transparent",
    boxShadow: "0 14px 34px rgba(23,32,51,0.08), inset 0 1px 0 rgba(255,255,255,0.62)",
    transform: "translateY(-2px)",
  },
  createSermonPlusButton: {
    alignItems: "center",
    background: "rgba(var(--color-primary-rgb),0.10)",
    border: "1px solid transparent",
    borderRadius: 999,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.65)",
    color: "var(--color-primary-dark)",
    display: "inline-flex",
    height: 92,
    justifyContent: "center",
    transition: "background 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease",
    width: 92,
  },
  createSermonPlusButtonHover: {
    background: "rgba(var(--color-primary-rgb),0.16)",
    boxShadow: "0 10px 24px rgba(var(--color-primary-rgb),0.18), inset 0 1px 0 rgba(255,255,255,0.78)",
    color: "var(--color-primary-dark)",
    transform: "scale(1.04)",
  },
  recentCardTop: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
  },
  recentCardTitle: {
    fontSize: 21,
    lineHeight: 1.16,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
  },
  recentCardPreview: {
    color: "#667085",
    fontSize: 13,
    lineHeight: 1.45,
  },
  recentCardDetailPanel: {
    alignSelf: "stretch",
    display: "grid",
    gap: 9,
    paddingTop: 2,
  },
  recentCardDetailRow: {
    alignItems: "center",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "72px minmax(0, 1fr)",
    minHeight: 22,
  },
  recentCardDetailLabel: {
    color: "#8a93a5",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  recentCardDetailValue: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 800,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  recentCardFooter: {
    alignItems: "center",
    color: SERMON_BRAND,
    display: "flex",
    flexWrap: "wrap",
    fontSize: 13,
    fontWeight: 900,
    gap: 8,
    justifyContent: "space-between",
    paddingTop: 2,
  },
  recentIconWrap: {
    alignItems: "center",
    background: "linear-gradient(135deg, #e8edff, #f4f7ff)",
    borderRadius: 14,
    display: "inline-flex",
    height: 56,
    justifyContent: "center",
    width: 56,
  },
  recentIcon: {
    height: 42,
    objectFit: "contain",
    width: 42,
  },
  sharedBadge: {
    background: "#e8edff",
    borderRadius: 999,
    color: "#25356f",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 800,
    padding: "4px 8px",
    width: "fit-content",
  },
  emptyRecent: {
    alignItems: "start",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    color: "#667085",
    display: "grid",
    gap: 10,
    minHeight: 190,
    padding: 22,
  },
  emptyIconWrap: {
    alignItems: "center",
    background: "#e8edff",
    borderRadius: 8,
    display: "inline-flex",
    height: 46,
    justifyContent: "center",
    width: 46,
  },
  emptyCreateButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #c8d2e5",
    borderRadius: 8,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 8,
    minHeight: 38,
    padding: "0 12px",
    width: "fit-content",
  },
  actionModalBackdrop: {
    alignItems: "center",
    background: "rgba(15,23,42,0.42)",
    display: "flex",
    inset: 0,
    justifyContent: "center",
    padding: 18,
    position: "fixed",
    zIndex: 1000,
  },
  actionModal: {
    background: "#ffffff",
    border: "1px solid rgba(255,255,255,0.72)",
    borderRadius: 8,
    boxShadow: "0 28px 70px rgba(15,23,42,0.28)",
    color: "#172033",
    display: "grid",
    gap: 18,
    maxWidth: 460,
    padding: 18,
    width: "min(100%, 460px)",
  },
  actionModalHeader: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
  },
  actionModalIconWrap: {
    alignItems: "center",
    background: "linear-gradient(135deg, #e8edff, #f4f7ff)",
    borderRadius: 8,
    display: "inline-flex",
    height: 58,
    justifyContent: "center",
    width: 58,
  },
  actionModalIcon: {
    height: 44,
    objectFit: "contain",
    width: 44,
  },
  actionModalClose: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    height: 38,
    justifyContent: "center",
    width: 38,
  },
  actionModalBody: {
    display: "grid",
    gap: 9,
  },
  scriptureModal: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxShadow: "0 30px 80px rgba(15,23,42,0.26)",
    color: "#172033",
    display: "grid",
    gap: 22,
    maxHeight: "min(92dvh, 720px)",
    maxWidth: 520,
    overflow: "auto",
    padding: 24,
    width: "min(100%, 520px)",
  },
  scriptureModalHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  scriptureModalTitleBlock: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    minWidth: 0,
  },
  scriptureModalIcon: {
    alignItems: "center",
    background: SERMON_BRAND,
    border: "1px solid transparent",
    borderRadius: 8,
    boxShadow: "0 12px 28px rgba(56,92,255,0.2)",
    color: "#ffffff",
    display: "inline-flex",
    height: 46,
    justifyContent: "center",
    width: 46,
  },
  scriptureModalTitle: {
    color: "#172033",
    fontSize: 24,
    fontWeight: 950,
    lineHeight: 1.05,
    margin: 0,
  },
  scriptureModalMeta: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.35,
    margin: "6px 0 0",
  },
  scriptureModalFields: {
    display: "grid",
    gap: 16,
  },
  scriptureField: {
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxSizing: "border-box",
    display: "grid",
    gap: 8,
    padding: "14px 16px",
  },
  scriptureFieldLabel: {
    color: "#475467",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0,
    lineHeight: 1,
  },
  scriptureInput: {
    background: "#ffffff",
    border: "1px solid #cfd8ea",
    borderRadius: 8,
    boxSizing: "border-box",
    color: "#172033",
    fontSize: 18,
    fontWeight: 850,
    minHeight: 48,
    outline: 0,
    padding: "0 13px",
    width: "100%",
  },
  scriptureSelect: {
    appearance: "none",
    background: "#ffffff",
    border: "1px solid #cfd8ea",
    borderRadius: 8,
    boxSizing: "border-box",
    color: "#172033",
    fontSize: 16,
    fontWeight: 850,
    minHeight: 48,
    outline: 0,
    padding: "0 13px",
    width: "100%",
  },
  scriptureFieldHint: {
    color: "#667085",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  scriptureModalActions: {
    alignItems: "center",
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    paddingTop: 2,
  },
  scriptureSecondaryButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #cfd8ea",
    borderRadius: 8,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    justifyContent: "center",
    minHeight: 44,
    padding: "0 16px",
  },
  scripturePrimaryButton: {
    alignItems: "center",
    background: SERMON_BRAND,
    border: "1px solid transparent",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 950,
    gap: 8,
    justifyContent: "center",
    minHeight: 44,
    padding: "0 18px",
  },
  actionModalTitle: {
    color: "#172033",
    fontSize: 24,
    lineHeight: 1.1,
    margin: 0,
  },
  actionModalMeta: {
    color: "#667085",
    fontSize: 13,
    fontWeight: 800,
    margin: 0,
  },
  actionModalPreview: {
    color: "#344054",
    fontSize: 14,
    lineHeight: 1.5,
    margin: 0,
  },
  actionModalActions: {
    display: "grid",
    gap: 10,
    gridTemplateColumns: "1fr 1fr",
  },
  actionModalSecondary: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 44,
    padding: "0 14px",
  },
  actionModalPrimary: {
    alignItems: "center",
    background: SERMON_BRAND,
    border: "1px solid transparent",
    borderRadius: 999,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 44,
    padding: "0 14px",
  },
  sermonEditorPage: {
    background: "#eef2f8",
    color: "#172033",
    display: "flex",
    height: "100dvh",
    minHeight: "100dvh",
    overflow: "hidden",
  },
  sermonEditorShell: {
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
    minHeight: "100dvh",
    minWidth: 0,
    overflow: "hidden",
    width: "100%",
  },
  sermonEditorTopbar: {
    alignItems: "center",
    background: "#ffffff",
    borderBottom: "1px solid #d9e1ef",
    display: "flex",
    flex: "0 0 auto",
    gap: 16,
    justifyContent: "space-between",
    minHeight: 82,
    padding: "6px 14px",
  },
  documentTitleRow: {
    alignItems: "center",
    display: "flex",
    gap: 8,
    minWidth: 0,
  },
  documentTitleLogoButton: {
    alignItems: "center",
    background: "transparent",
    border: 0,
    cursor: "pointer",
    display: "inline-flex",
    flex: "0 0 auto",
    justifyContent: "center",
    padding: 0,
  },
  documentTitleLogo: {
    height: 58,
    objectFit: "contain",
    width: 58,
  },
  documentTitleDivider: {
    background: "#d9e1ef",
    borderRadius: 999,
    flex: "0 0 auto",
    height: 46,
    width: 1,
  },
  documentTitleIcon: {
    flex: "0 0 auto",
    height: 34,
    objectFit: "contain",
    width: 34,
  },
  documentNameBlock: {
    display: "grid",
    flex: "1 1 auto",
    gap: 2,
    minWidth: 0,
  },
  titleInput: {
    border: 0,
    color: "#172033",
    fontSize: 21,
    fontWeight: 900,
    minWidth: 0,
    outline: 0,
    width: "min(52vw, 620px)",
  },
  ownerLine: {
    color: "#667085",
    fontSize: 13,
  },
  editorActionGroup: {
    alignItems: "center",
    display: "flex",
    flex: "0 0 auto",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },
  editorPrimaryButton: {
    alignItems: "center",
    background: SERMON_BRAND,
    border: 0,
    borderRadius: 999,
    color: "#fff",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 8,
    minHeight: 38,
    padding: "0 14px",
  },
  editorGhostButton: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 8,
    minHeight: 38,
    padding: "0 12px",
  },
  editorDangerButton: {
    alignItems: "center",
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    borderRadius: 999,
    color: "#be123c",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 8,
    minHeight: 38,
    padding: "0 12px",
  },
  editorToolbar: {
    alignItems: "center",
    alignSelf: "stretch",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    boxSizing: "border-box",
    boxShadow: "0 10px 24px rgba(23,32,51,0.07)",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "space-between",
    margin: "10px 14px 0",
    padding: "8px 10px",
  },
  editorToolGroup: {
    alignItems: "center",
    display: "flex",
    flex: "1 1 360px",
    flexWrap: "wrap",
    gap: 8,
    minWidth: 0,
  },
  editorMetaGroup: {
    alignItems: "center",
    display: "flex",
    flex: "0 1 auto",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
    marginLeft: "auto",
    minWidth: 0,
  },
  editorToolButton: {
    alignItems: "center",
    background: "#fff",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 7,
    minHeight: 36,
    padding: "0 12px",
  },
  editorToolbarDivider: {
    background: "#d9e1ef",
    height: 28,
    width: 1,
  },
  inlineField: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#667085",
    display: "flex",
    flex: "1 1 240px",
    fontSize: 12,
    fontWeight: 900,
    gap: 8,
    minHeight: 36,
    padding: "0 12px",
  },
  inlineInput: {
    background: "transparent",
    border: 0,
    color: "#172033",
    flex: 1,
    font: "inherit",
    fontSize: 14,
    fontWeight: 700,
    minWidth: 0,
    outline: 0,
  },
  inlineSelect: {
    background: "#fff",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    fontWeight: 800,
    minHeight: 36,
    padding: "0 10px",
  },
  editorNotice: {
    background: "#dcfce7",
    color: "#166534",
    flex: "0 0 auto",
    fontWeight: 800,
    margin: "10px 14px 0",
    padding: "10px 14px",
  },
  sermonWorkspace: {
    display: "grid",
    flex: "1 1 auto",
    gap: 18,
    gridTemplateColumns: "minmax(0, 1fr)",
    minHeight: 0,
    overflowY: "auto",
    padding: "14px 14px 108px",
  },
  sermonWorkspacePhone: {
    gridTemplateColumns: "1fr",
  },
  quickAddRail: {
    alignItems: "stretch",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 16,
    boxShadow: "0 14px 28px rgba(23,32,51,0.08)",
    bottom: 14,
    boxSizing: "border-box",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    left: "50%",
    maxWidth: 980,
    padding: 12,
    position: "fixed",
    transform: "translateX(-50%)",
    width: "calc(100% - 28px)",
    zIndex: 3,
  },
  quickAddRailPhone: {
    bottom: "calc(10px + env(safe-area-inset-bottom))",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    left: 10,
    right: 10,
    transform: "none",
    width: "auto",
  },
  quickAddRailLabel: {
    color: "#667085",
    gridColumn: "1 / -1",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.08em",
    padding: "0 2px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  quickAddButton: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 12,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 40,
    minWidth: 0,
    padding: "0 8px",
    textAlign: "left",
    whiteSpace: "nowrap",
    width: "auto",
  },
  quickAddButtonPhone: {
    justifyContent: "center",
    width: "auto",
  },
  sermonDocumentPane: {
    minWidth: 0,
  },
  sermonPaper: {
    alignContent: "start",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxShadow: "0 18px 42px rgba(23,32,51,0.08)",
    display: "grid",
    gap: 8,
    margin: "0 auto",
    maxWidth: 980,
    minHeight: "calc(100dvh - 150px)",
    padding: "20px clamp(18px, 3vw, 42px) 28px",
  },
  paperField: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#64748b",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 34,
    padding: "0 12px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  paperInput: {
    background: "transparent",
    border: 0,
    borderRadius: 0,
    color: "#172033",
    font: "inherit",
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.2,
    minHeight: 24,
    minWidth: 0,
    outline: 0,
    padding: 0,
    textAlign: "center",
    textTransform: "none",
    width: "auto",
  },
  paperSpeakerInput: {
    maxWidth: 180,
    width: "min(42vw, 180px)",
  },
  paperDateInput: {
    maxWidth: 132,
    width: 132,
  },
  metaToggle: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#64748b",
    cursor: "pointer",
    display: "inline-flex",
    flex: "0 0 auto",
    fontSize: 11,
    fontWeight: 900,
    gap: 9,
    justifyContent: "center",
    minHeight: 34,
    padding: "0 10px 0 12px",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  switchInput: {
    height: 1,
    opacity: 0,
    position: "absolute",
    width: 1,
  },
  switchTrack: {
    alignItems: "center",
    background: "#cbd5e1",
    borderRadius: 999,
    display: "inline-flex",
    height: 20,
    padding: 2,
    transition: "background 150ms ease",
    width: 38,
  },
  switchTrackOn: {
    background: SERMON_BRAND,
  },
  switchThumb: {
    background: "#ffffff",
    borderRadius: 999,
    boxShadow: "0 1px 3px rgba(15,23,42,0.24)",
    display: "block",
    height: 16,
    transform: "translateX(0)",
    transition: "transform 150ms ease",
    width: 16,
  },
  switchThumbOn: {
    transform: "translateX(18px)",
  },
  titleSlideBlock: {
    alignItems: "center",
    alignSelf: "start",
    borderBottom: "1px solid #e5eaf3",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    paddingBottom: 10,
  },
  blockEyebrow: {
    color: SERMON_BRAND,
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  paperTitleButton: {
    background: "transparent",
    border: 0,
    color: "#172033",
    cursor: "text",
    display: "block",
    font: "inherit",
    fontSize: "clamp(34px, 5vw, 58px)",
    fontWeight: 900,
    lineHeight: 1.02,
    margin: "2px 0 4px",
    padding: 0,
    textAlign: "left",
    width: "100%",
  },
  paperTitleInput: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    color: "#172033",
    display: "block",
    font: "inherit",
    fontSize: "clamp(34px, 5vw, 58px)",
    fontWeight: 900,
    lineHeight: 1.02,
    margin: "2px 0 4px",
    minWidth: 0,
    outline: 0,
    padding: "6px 8px",
    width: "min(100%, 760px)",
  },
  paperSubtitle: {
    color: "#667085",
    fontSize: 14,
    fontWeight: 800,
    margin: 0,
  },
  compactToggle: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#475569",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    gap: 8,
    minHeight: 32,
    padding: "0 10px",
    whiteSpace: "nowrap",
  },
  manualVerseBox: {
    background: "#f8faff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    padding: 14,
  },
  blockList: {
    display: "grid",
    gap: 14,
  },
  sermonBlock: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxShadow: "0 10px 24px rgba(23,32,51,0.06)",
    display: "grid",
    gap: 14,
    padding: 16,
  },
  noteBlock: {
    gap: 8,
  },
  visualBlock: {
    background: "#f8fbff",
  },
  textSlideBlock: {
    background: "#ffffff",
    borderColor: "#cdd8ff",
  },
  headerBlock: {
    background: "#ffffff",
    borderColor: "#d9e1ef",
    padding: "18px 18px 20px",
  },
  sermonBlockSelected: {
    borderColor: "#9fb2ff",
    boxShadow: "0 14px 32px rgba(56,92,255,0.13)",
  },
  sermonBlockDragging: {
    opacity: 0.62,
  },
  sermonBlockDragOver: {
    borderColor: SERMON_BRAND,
    boxShadow: "0 0 0 3px rgba(56,92,255,0.16), 0 14px 32px rgba(56,92,255,0.13)",
  },
  blockHeader: {
    alignItems: "flex-start",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  blockHeaderContent: {
    minWidth: 0,
    flex: "1 1 auto",
  },
  blockTitleInput: {
    background: "transparent",
    border: 0,
    color: "#172033",
    fontSize: 20,
    fontWeight: 900,
    marginTop: 4,
    outline: 0,
    textTransform: "none",
    width: "100%",
  },
  headerTitleInput: {
    color: "#0f172a",
    fontSize: "clamp(28px, 4vw, 46px)",
    letterSpacing: 0,
    lineHeight: 1.02,
    overflow: "hidden",
    textOverflow: "clip",
    whiteSpace: "nowrap",
  },
  visualSlideBody: {
    alignItems: "center",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "auto minmax(0, 1fr)",
  },
  textSlideTextarea: {
    background: "#f8faff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    color: "#172033",
    font: "inherit",
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.45,
    minHeight: 180,
    outline: 0,
    padding: 14,
    resize: "vertical",
    whiteSpace: "pre-wrap",
    width: "100%",
  },
  visualUploadButton: {
    alignItems: "center",
    background: "#eef3ff",
    border: "1px solid #cdd8ff",
    borderRadius: 999,
    color: SERMON_BRAND,
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 13,
    fontWeight: 900,
    gap: 7,
    justifyContent: "center",
    minHeight: 38,
    padding: "0 12px",
    position: "relative",
    width: "fit-content",
  },
  hiddenFileInput: {
    height: 1,
    opacity: 0,
    overflow: "hidden",
    position: "absolute",
    width: 1,
  },
  visualUrlInput: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    boxSizing: "border-box",
    color: "#172033",
    fontSize: 13,
    fontWeight: 800,
    minHeight: 38,
    minWidth: 0,
    outline: 0,
    padding: "0 12px",
    width: "100%",
  },
  visualPreviewFrame: {
    alignItems: "center",
    background: "#0f172a",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    display: "flex",
    justifyContent: "center",
    minHeight: 180,
    overflow: "hidden",
  },
  visualPreviewImage: {
    display: "block",
    height: "100%",
    maxHeight: 360,
    maxWidth: "100%",
    objectFit: "contain",
    width: "100%",
  },
  visualSlidePreviewShell: {
    background: "#0f172a",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxShadow: "0 18px 34px rgba(15,23,42,0.12)",
    overflow: "hidden",
    padding: 8,
  },
  visualSlidePreviewStage: {
    aspectRatio: "16 / 9",
    background: "#020617",
    borderRadius: 6,
    overflow: "hidden",
    position: "relative",
    width: "100%",
  },
  visualSlidePreviewImage: {
    display: "block",
    height: "100%",
    width: "100%",
  },
  visualSlideCaptionPreview: {
    background: "linear-gradient(180deg, rgba(15,23,42,0), rgba(15,23,42,0.78))",
    bottom: 0,
    boxSizing: "border-box",
    color: "#ffffff",
    fontSize: "clamp(13px, 2vw, 20px)",
    fontWeight: 850,
    left: 0,
    lineHeight: 1.25,
    padding: "clamp(24px, 5vw, 54px) clamp(14px, 3vw, 30px) clamp(12px, 2.2vw, 22px)",
    position: "absolute",
    right: 0,
    textShadow: "0 2px 10px rgba(0,0,0,0.4)",
    whiteSpace: "pre-wrap",
  },
  visualAdjustControls: {
    background: "#f8fafc",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    display: "grid",
    gap: 10,
    gridTemplateColumns: "minmax(120px, 0.8fr) repeat(2, minmax(0, 1fr))",
    padding: 12,
  },
  visualControlField: {
    color: "#475569",
    display: "grid",
    fontSize: 12,
    fontWeight: 900,
    gap: 7,
    minWidth: 0,
    textTransform: "uppercase",
  },
  visualControlSelect: {
    background: "#ffffff",
    border: "1px solid #cfd8ea",
    borderRadius: 8,
    color: "#172033",
    fontSize: 14,
    fontWeight: 850,
    minHeight: 38,
    outline: 0,
    padding: "0 10px",
    width: "100%",
  },
  visualControlRange: {
    accentColor: SERMON_BRAND,
    minHeight: 38,
    width: "100%",
  },
  visualEmptyPreview: {
    alignItems: "center",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: 8,
    color: "#64748b",
    display: "flex",
    fontSize: 13,
    fontWeight: 800,
    gap: 8,
    justifyContent: "center",
    minHeight: 120,
    textAlign: "center",
  },
  noteToolbar: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: -2,
  },
  noteToolbarButton: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    gap: 4,
    height: 30,
    justifyContent: "center",
    minWidth: 32,
    padding: "0 8px",
  },
  thoughtBubbleToolbarButton: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
  },
  blockControls: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    justifyContent: "flex-end",
  },
  dragHandleButton: {
    alignItems: "center",
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
    borderRadius: 999,
    color: SERMON_BRAND,
    cursor: "grab",
    display: "inline-flex",
    height: 30,
    justifyContent: "center",
    padding: 0,
    touchAction: "none",
    width: 30,
  },
  miniButton: {
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    minHeight: 30,
    padding: "0 10px",
  },
  miniButtonDisabled: {
    cursor: "not-allowed",
    opacity: 0.45,
  },
  liveSendButton: {
    alignItems: "center",
    background: "#eef8f5",
    border: "1px solid #b9e4d6",
    borderRadius: 999,
    color: "#0f766e",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    gap: 6,
    minHeight: 30,
    padding: "0 10px",
    whiteSpace: "nowrap",
  },
  liveSendButtonActive: {
    background: "#0f766e",
    borderColor: "#0f766e",
    color: "#ffffff",
  },
  miniDangerButton: {
    background: "#fff5f5",
    border: "1px solid #fecdd3",
    borderRadius: 999,
    color: "#be123c",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    minHeight: 30,
    padding: "0 10px",
  },
  verseSlideList: {
    display: "grid",
    gap: 8,
  },
  verseLine: {
    alignItems: "flex-start",
    background: "#f8faff",
    border: "1px solid #e5eaf3",
    borderRadius: 8,
    color: "#344054",
    display: "grid",
    gap: 10,
    gridTemplateColumns: "auto 34px minmax(0, 1fr)",
    lineHeight: 1.5,
    padding: 10,
  },
  verseLineNumber: {
    color: SERMON_BRAND,
    fontWeight: 900,
  },
  noteBlockTextarea: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxSizing: "border-box",
    color: "#172033",
    direction: "ltr",
    font: "inherit",
    lineHeight: 1.6,
    minHeight: 150,
    outline: 0,
    overflow: "visible",
    padding: 12,
    textAlign: "left",
    textTransform: "none",
    unicodeBidi: "isolate",
    whiteSpace: "pre-wrap",
  },
  noteBlockTextareaEmpty: {
    background: "#ffffff",
  },
  emptyEditorBlock: {
    alignItems: "center",
    background: "#f8faff",
    border: "1px dashed #bdc9df",
    borderRadius: 8,
    color: "#667085",
    display: "flex",
    gap: 14,
    padding: "18px 20px",
  },
  emptyEditorIcon: {
    alignItems: "center",
    background: "#eef3ff",
    border: "1px solid #dbe5ff",
    borderRadius: 8,
    color: SERMON_BRAND,
    display: "inline-flex",
    flex: "0 0 auto",
    height: 46,
    justifyContent: "center",
    width: 46,
  },
  emptyEditorTitle: {
    color: "#172033",
    display: "block",
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.25,
  },
  emptyEditorText: {
    color: "#667085",
    display: "block",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.45,
    marginTop: 3,
  },
  viewerPreviewPane: {
    minWidth: 0,
  },
  viewerPreviewShell: {
    position: "sticky",
    top: 14,
  },
  viewerDeviceHeader: {
    alignItems: "center",
    color: "#344054",
    display: "flex",
    fontWeight: 900,
    gap: 8,
    marginBottom: 8,
  },
  viewerScreen: {
    background: "#111827",
    border: "10px solid #172033",
    borderRadius: 26,
    boxShadow: "0 20px 48px rgba(23,32,51,0.22)",
    color: "#ffffff",
    display: "grid",
    gap: 10,
    minHeight: 560,
    padding: 16,
  },
  viewerOutputCard: {
    alignItems: "center",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 14,
    display: "grid",
    gap: 10,
    gridTemplateColumns: "34px minmax(0, 1fr)",
    padding: 12,
  },
  viewerOutputIndex: {
    alignItems: "center",
    background: SERMON_BRAND,
    borderRadius: 999,
    display: "inline-flex",
    fontWeight: 900,
    height: 30,
    justifyContent: "center",
    width: 30,
  },
  viewerOutputTitle: {
    fontWeight: 900,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  viewerOutputMeta: {
    color: "rgba(255,255,255,0.62)",
    fontSize: 12,
    fontWeight: 800,
    marginTop: 3,
  },
  viewerEmpty: {
    alignSelf: "center",
    color: "rgba(255,255,255,0.66)",
    fontWeight: 800,
    justifySelf: "center",
    textAlign: "center",
  },
  readerPage: {
    background: "#f4f6fb",
    color: "#172033",
    minHeight: "100dvh",
    padding: "14px clamp(12px, 2vw, 22px) calc(104px + env(safe-area-inset-bottom))",
  },
  readerTopbar: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxShadow: "0 12px 28px rgba(23,32,51,0.08)",
    display: "grid",
    gap: 12,
    gridTemplateColumns: "auto minmax(0, 1fr) auto",
    margin: "0 auto",
    maxWidth: 1180,
    padding: 12,
  },
  readerTopbarPhone: {
    gridTemplateColumns: "1fr",
  },
  readerTitleBlock: {
    display: "grid",
    gap: 3,
    minWidth: 0,
    textAlign: "center",
  },
  readerKicker: {
    color: SERMON_BRAND,
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  readerTitle: {
    color: "#172033",
    fontSize: "clamp(22px, 3vw, 34px)",
    lineHeight: 1.05,
    margin: 0,
  },
  readerMeta: {
    color: "#667085",
    fontSize: 14,
    fontWeight: 800,
    margin: 0,
  },
  readerGhostButton: {
    alignItems: "center",
    background: "#f6f8fc",
    border: "1px solid #d9e1ef",
    borderRadius: 999,
    color: "#172033",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 900,
    gap: 8,
    justifyContent: "center",
    minHeight: 42,
    padding: "0 14px",
    whiteSpace: "nowrap",
  },
  readerStatus: {
    background: "#dcfce7",
    border: "1px solid #bbf7d0",
    borderRadius: 8,
    color: "#166534",
    fontWeight: 900,
    margin: "12px auto 0",
    maxWidth: 1180,
    padding: "11px 14px",
  },
  readerStatusError: {
    background: "#fff1f2",
    borderColor: "#fecdd3",
    color: "#be123c",
  },
  readerShell: {
    alignItems: "start",
    display: "block",
    margin: "16px auto 0",
    maxWidth: 1180,
  },
  readerBottomActions: {
    alignItems: "center",
    backdropFilter: "blur(16px)",
    background: "rgba(244, 246, 251, 0.9)",
    borderTop: "1px solid rgba(217, 225, 239, 0.92)",
    bottom: 0,
    boxSizing: "border-box",
    display: "flex",
    gap: 10,
    justifyContent: "center",
    left: 0,
    padding: "12px 14px calc(12px + env(safe-area-inset-bottom))",
    position: "fixed",
    right: 0,
    zIndex: 40,
  },
  readerBottomButton: {
    alignItems: "center",
    background: SERMON_BRAND,
    border: "1px solid rgba(56, 92, 255, 0.82)",
    borderRadius: 999,
    boxShadow: "0 14px 34px rgba(56, 92, 255, 0.24)",
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontWeight: 950,
    gap: 8,
    justifyContent: "center",
    minHeight: 46,
    minWidth: 230,
    padding: "0 18px",
    whiteSpace: "nowrap",
  },
  readerBottomButtonMuted: {
    background: "#ffffff",
    borderColor: "#d9e1ef",
    boxShadow: "0 14px 30px rgba(23,32,51,0.1)",
    color: "#172033",
  },
  readerDocument: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxShadow: "0 18px 42px rgba(23,32,51,0.09)",
    display: "grid",
    gap: 18,
    minHeight: "calc(100dvh - 128px)",
    padding: "clamp(20px, 4vw, 46px)",
  },
  readerBlock: {
    background: "#ffffff",
    border: "1px solid #e5eaf3",
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    gap: 14,
    padding: "clamp(16px, 3vw, 28px)",
  },
  readerHeaderBlock: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    gap: 12,
    padding: "clamp(18px, 3.5vw, 34px)",
  },
  readerBlockActive: {
    borderColor: "#9fb2ff",
    boxShadow: "0 16px 36px rgba(56,92,255,0.12)",
  },
  readerBlockHeader: {
    alignItems: "start",
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
  },
  readerBlockKicker: {
    color: SERMON_BRAND,
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
  },
  readerHeadline: {
    color: "#172033",
    fontSize: "clamp(36px, 6vw, 68px)",
    lineHeight: 1.02,
    margin: "4px 0 0",
  },
  readerSubhead: {
    color: "#667085",
    fontSize: 18,
    fontWeight: 800,
    margin: 0,
  },
  readerScriptureTitle: {
    color: "#172033",
    fontSize: "clamp(28px, 4.5vw, 46px)",
    lineHeight: 1.08,
    margin: "4px 0 0",
  },
  readerHeaderTitle: {
    color: "#0f172a",
    fontSize: "clamp(34px, 6vw, 72px)",
    lineHeight: 1.02,
    margin: 0,
    overflow: "hidden",
    textOverflow: "clip",
    whiteSpace: "nowrap",
  },
  readerLiveButton: {
    alignItems: "center",
    background: SERMON_BRAND,
    border: "1px solid transparent",
    borderRadius: 999,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 900,
    gap: 8,
    minHeight: 42,
    padding: "0 14px",
    whiteSpace: "nowrap",
  },
  readerLiveButtonActive: {
    background: "#166534",
  },
  readerVerseList: {
    display: "grid",
    gap: 14,
  },
  readerVerse: {
    alignItems: "start",
    color: "#1f2937",
    display: "grid",
    fontSize: "clamp(24px, 3.4vw, 38px)",
    gap: 12,
    gridTemplateColumns: "42px minmax(0, 1fr)",
    lineHeight: 1.32,
  },
  readerVerseDisabled: {
    color: "#98a2b3",
  },
  readerVerseNumber: {
    color: SERMON_BRAND,
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.6,
  },
  readerNoteTitle: {
    color: "#172033",
    fontSize: "clamp(26px, 4vw, 42px)",
    lineHeight: 1.1,
    margin: "4px 0 0",
  },
  readerNoteBody: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxSizing: "border-box",
    color: "#344054",
    direction: "ltr",
    fontSize: "clamp(22px, 3vw, 34px)",
    lineHeight: 1.45,
    margin: 0,
    minHeight: 120,
    overflow: "visible",
    padding: 18,
    textAlign: "left",
    unicodeBidi: "isolate",
    whiteSpace: "pre-wrap",
  },
  readerTextSlideBody: {
    background: "#f8faff",
    border: "1px solid #cdd8ff",
    borderRadius: 8,
    boxSizing: "border-box",
    color: "#172033",
    direction: "ltr",
    fontSize: "clamp(24px, 3.4vw, 40px)",
    fontWeight: 850,
    lineHeight: 1.3,
    margin: 0,
    minHeight: 160,
    padding: 20,
    textAlign: "left",
    unicodeBidi: "isolate",
    whiteSpace: "pre-wrap",
  },
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 14,
  },

  topBar: {
    alignItems: "center",
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
  },
  topBarPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },

  heading: {
    color: "#0f172a",
    fontSize: "clamp(20px, 1.6vw, 24px)",
    fontWeight: 700,
  },

  saveState: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 4,
  },

  topActions: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-end",
  },
  topActionsPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },

  primaryAction: {
    background: "#5F7D4D",
    border: "none",
    borderRadius: 12,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
    padding: "12px 16px",
  },

  secondaryAction: {
    background: "rgba(var(--color-primary-rgb),0.10)",
    border: "1px solid rgba(var(--color-primary-rgb),0.18)",
    borderRadius: 12,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    fontWeight: 700,
    padding: "12px 16px",
  },

  statusBanner: {
    background: "#eef6ea",
    border: "1px solid #cfe1c5",
    borderRadius: 14,
    color: "var(--color-primary-dark)",
    fontSize: 13,
    fontWeight: 600,
    padding: "10px 14px",
  },

  dashboardLayout: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  dashboardHero: {
    alignItems: "center",
    background: "linear-gradient(135deg, #f2f8ee, #ffffff)",
    border: "1px solid #d8e5d0",
    borderRadius: 20,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
    padding: 20,
  },
  dashboardHeroPhone: {
    alignItems: "stretch",
    flexDirection: "column",
  },

  dashboardTitle: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 800,
  },

  dashboardText: {
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.6,
    marginTop: 6,
    maxWidth: 620,
  },

  dashboardActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  dashboardActionsPhone: {
    flexDirection: "column",
  },

  librarySection: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  sectionTitle: {
    color: "inherit",
    fontSize: 17,
    fontWeight: 900,
    margin: 0,
  },

  sermonTable: {
    background: "#ffffff",
    border: "1px solid #d8e5d0",
    borderRadius: 16,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
  },

  sermonRow: {
    alignItems: "center",
    borderBottom: "1px solid #e2eadc",
    display: "grid",
    gap: 12,
    gridTemplateColumns: "minmax(220px, 1.35fr) minmax(120px, 0.7fr) minmax(105px, 0.55fr) minmax(115px, 0.65fr) minmax(320px, 1fr)",
    padding: "13px 14px",
  },
  sermonHeaderRow: {
    background: "#f7faf5",
    color: "#506044",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0,
    textTransform: "uppercase",
  },
  sermonRowPhone: {
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    gap: 9,
    padding: 14,
  },

  sermonTitleCell: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    gap: 7,
  },

  sermonBadge: {
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    padding: "6px 10px",
    textTransform: "uppercase",
  },

  savedBadge: {
    background: "#f8fafc",
    color: "#475569",
  },

  sermonCardTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 800,
  },

  sermonCell: {
    color: "#64748b",
    fontSize: 13,
  },

  sermonCardActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    justifyContent: "flex-end",
  },
  sermonActionsHeader: {
    textAlign: "right",
  },

  tableActionButton: {
    background: "rgba(var(--color-primary-rgb),0.10)",
    border: "1px solid rgba(var(--color-primary-rgb),0.18)",
    borderRadius: 10,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "9px 11px",
  },
  tablePrimaryButton: {
    background: "#5F7D4D",
    border: "none",
    borderRadius: 10,
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "9px 11px",
  },
  tableDeleteButton: {
    background: "#fff7ed",
    border: "1px solid #fdba74",
    borderRadius: 10,
    color: "#c2410c",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 800,
    padding: "9px 11px",
  },

  emptyState: {
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.5,
  },

  setupGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  setupGridPhone: {
    gridTemplateColumns: "1fr",
  },

  setupCard: {
    background: "#ffffff",
    border: "1px solid #d8e5d0",
    borderRadius: 16,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 14,
  },

  builderLayout: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "330px minmax(0, 1fr)",
  },
  builderLayoutPhone: {
    gridTemplateColumns: "1fr",
  },

  liveLayout: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "280px minmax(0, 1fr)",
  },
  liveLayoutPhone: {
    gridTemplateColumns: "1fr",
  },

  liveSidebar: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  liveContent: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  sidebar: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  sidebarCard: {
    background: "#ffffff",
    border: "1px solid #d8e5d0",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 14,
  },

  sidebarTitle: {
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 700,
  },

  label: {
    color: "#334155",
    fontSize: 13,
    fontWeight: 600,
  },

  input: {
    border: "1px solid #cbd5c0",
    borderRadius: 10,
    fontSize: 14,
    padding: "10px 12px",
  },

  textarea: {
    border: "1px solid #cbd5c0",
    borderRadius: 10,
    fontSize: 14,
    minHeight: 90,
    padding: "10px 12px",
    resize: "vertical",
  },

  largeTextarea: {
    border: "1px solid #cbd5c0",
    borderRadius: 12,
    fontSize: 14,
    minHeight: 180,
    padding: "12px 14px",
    resize: "vertical",
  },


  primaryWideButton: {
    background: "#5F7D4D",
    border: "none",
    borderRadius: 12,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
    padding: "12px 14px",
  },

  secondaryWideButton: {
    background: "rgba(var(--color-primary-rgb),0.10)",
    border: "1px solid rgba(var(--color-primary-rgb),0.18)",
    borderRadius: 12,
    color: "var(--color-primary-dark)",
    cursor: "pointer",
    fontWeight: 700,
    padding: "12px 14px",
  },

  deleteWideButton: {
    background: "#fff7ed",
    border: "1px solid #fdba74",
    borderRadius: 12,
    color: "#c2410c",
    cursor: "pointer",
    fontWeight: 700,
    padding: "12px 14px",
  },

  timelineItem: {
    alignItems: "center",
    background: "#f7faf5",
    border: "1px solid #dbe8d4",
    borderRadius: 14,
    cursor: "pointer",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    padding: "12px 14px",
  },

  timelineItemActive: {
    background: "#eaf4e5",
    border: "1px solid #9eb88e",
  },

  timelineIndex: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 3,
    textTransform: "uppercase",
  },

  timelineLabel: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
  },

  timelineMeta: {
    color: "#64748b",
    fontSize: 12,
    marginTop: 4,
  },

  timelineActions: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  reorderButton: {
    background: "#fff",
    border: "1px solid #cbd5c0",
    borderRadius: 10,
    color: "#475569",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 8px",
  },

  removeButton: {
    background: "#fff7ed",
    border: "1px solid #fdba74",
    borderRadius: 10,
    color: "#c2410c",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    padding: "6px 8px",
  },

  liveTimelineItem: {
    alignItems: "center",
    background: "#f7faf5",
    border: "1px solid #dbe8d4",
    borderRadius: 12,
    color: "#0f172a",
    cursor: "pointer",
    display: "flex",
    gap: 10,
    padding: "12px 14px",
    textAlign: "left",
  },

  liveTimelineItemActive: {
    background: "#eaf4e5",
    border: "1px solid #9eb88e",
  },

  liveTimelineIndex: {
    color: "#5F7D4D",
    fontSize: 12,
    fontWeight: 800,
    minWidth: 16,
  },

  liveTimelineLabel: {
    fontSize: 14,
    fontWeight: 700,
  },

  editorPanel: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  editorCard: {
    background: "#ffffff",
    border: "1px solid #d8e5d0",
    borderRadius: 18,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 16,
  },

  editorTitle: {
    color: "#0f172a",
    fontSize: 18,
    fontWeight: 700,
  },

  editorHeaderRow: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },

  editorTitleSmall: {
    color: "#334155",
    fontSize: 14,
    fontWeight: 700,
    marginTop: 8,
  },

  previewCard: {
    background: "linear-gradient(135deg, var(--color-primary), var(--color-primary-dark))",
    borderRadius: 18,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    justifyContent: "center",
    minHeight: 170,
    padding: "24px 26px",
  },

  previewHeadline: {
    fontSize: "clamp(28px, 3vw, 40px)",
    fontWeight: 800,
    lineHeight: 1.1,
  },

  previewSubline: {
    fontSize: 18,
    opacity: 0.92,
  },

  previewDate: {
    fontSize: 14,
    opacity: 0.78,
  },

  slidePreviewList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  slideCard: {
    background: "#f8faf5",
    border: "1px solid #dbe8d4",
    borderRadius: 14,
    padding: 12,
  },

  slideCardHeader: {
    alignItems: "center",
    display: "flex",
    gap: 10,
    justifyContent: "space-between",
    marginBottom: 6,
  },

  slideReference: {
    color: "var(--color-primary-dark)",
    fontSize: 13,
    fontWeight: 700,
  },

  slideText: {
    alignItems: "flex-start",
    color: "#334155",
    display: "flex",
    fontSize: 14,
    gap: 8,
    lineHeight: 1.5,
  },

  slideVerseNumber: {
    color: "#5F7D4D",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.3,
    marginTop: 2,
    minWidth: 16,
  },

  warningBox: {
    background: "#fff7ed",
    border: "1px solid #fdba74",
    borderRadius: 12,
    color: "#9a3412",
    fontSize: 13,
    lineHeight: 1.5,
    padding: "10px 12px",
  },

  toggleRow: {
    alignItems: "center",
    display: "flex",
    gap: 8,
  },

  toggleLabel: {
    color: "#475569",
    fontSize: 12,
    fontWeight: 700,
  },

  liveStage: {
    background: "#ffffff",
    border: "1px solid #d8e5d0",
    borderRadius: 20,
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    padding: 20,
  },

  liveTitleCard: {
    alignItems: "center",
    background: "linear-gradient(135deg, var(--color-primary), var(--color-primary-dark))",
    borderRadius: 20,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    justifyContent: "center",
    minHeight: 280,
    padding: 28,
    textAlign: "center",
  },

  liveTitleHeadline: {
    fontSize: "clamp(36px, 4vw, 52px)",
    fontWeight: 800,
    lineHeight: 1.05,
  },

  liveTitleMeta: {
    fontSize: 20,
    opacity: 0.92,
  },

  liveTitleDate: {
    fontSize: 15,
    opacity: 0.8,
  },

  liveSlidesList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  liveSlideCard: {
    background: "#f8faf5",
    border: "1px solid #dbe8d4",
    borderRadius: 18,
    padding: 20,
  },

  liveSlideReference: {
    color: "var(--color-primary-dark)",
    fontSize: 14,
    fontWeight: 800,
    marginBottom: 10,
  },

  liveSlideText: {
    color: "#0f172a",
    display: "flex",
    fontSize: "clamp(22px, 2vw, 30px)",
    gap: 12,
    lineHeight: 1.5,
  },

  liveVerseNumber: {
    color: "#5F7D4D",
    fontSize: 14,
    fontWeight: 800,
    minWidth: 20,
    paddingTop: 6,
  },

  liveCustomCard: {
    background: "#f8faf5",
    border: "1px solid #dbe8d4",
    borderRadius: 18,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 220,
    padding: 22,
  },

  liveCustomTitle: {
    color: "#0f172a",
    fontSize: 28,
    fontWeight: 800,
  },

  liveCustomBody: {
    color: "#334155",
    fontSize: 20,
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
  },

};
